import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { alertSummaryPollInterval } from '../lib/alertSummary';
import type {
  PlatformNotification,
  PlatformWorker,
  PlatformWorkerRun,
  PlatformWorkerStats,
  Project,
} from '../types/platform';
import { normalizeProjectStatus, sanitizeProject } from '../lib/projectStatus';
import { bucketLastNDays } from '../lib/sparklineBuckets';
import type { DataGenerationMeta } from '../lib/dataTrust';
import type { RcaOwner, RcaOwnershipRecord } from '../lib/rcaEngineView';
import { getMyEmail } from '../lib/identity';
import { ApiError, httpStatusMessage, isHtmlErrorBody } from '../lib/apiError';
import { syncIncidentRead } from './incidentReadSync';
import { handleUnauthorizedResponse } from '../auth/authEvents';
import {
  decodeSchemaEnvelope,
  encodeSchemaEnvelope,
  SCHEMA_CODEC_HEADERS,
  type SchemaEnvelope,
} from '../utils/schemaCodec';
import { normalizeRepoSpecs } from '../utils/repoUrl';
import {
  pollIntervalMs,
  refetchWhenActive,
  tasksStillRunning,
  understandingStillRunning,
  workflowNeedsFastPoll,
} from '../lib/queryPolling';
import { amsWorkflowNeedsFastPoll } from '../lib/amsWorkflowAuthority';
import {
  isIngestionFailed,
  shouldPollProjectDuringIngestion,
  projectPatchForUploadStarted,
  shouldShowIngestionGate,
} from '../lib/ingestionGate';
import { lockBootstrapGate, clearBootstrapGateLock } from '../lib/bootstrapGateLock';
import { bytesUploadedAfterChunk } from '../lib/archiveUploadProgress';
import { fetchWithTimeout, type TimedRequestInit } from '../lib/fetchWithTimeout';
import type { OpsExecutionSnapshot } from '../lib/opsRecoveryProgress';
import { randomId } from '../lib/randomId';

// In dev, use same-origin /api/v1 so Vite proxies to the API container.
// In production, default to same-origin /api/v1 (nginx or ingress proxies to API).
const API_BASE =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? '/api/v1' : '/api/v1');

const API_V2_BASE =
  import.meta.env.VITE_API_V2_URL ||
  (import.meta.env.DEV ? '/api/v2' : '/api/v2');

/** Raw `/api/v2` fetcher: session JWT (or automation key), central 401 handling, JSON body. */
export async function apiV2Client<T>(path: string, options: TimedRequestInit = {}): Promise<T> {
  const hasJsonBody = typeof options.body === 'string';
  const res = await fetchWithTimeout(`${API_V2_BASE}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(options.headers as Record<string, string>),
    },
  });
  handleUnauthorizedResponse(res.status, path);
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(v2ErrorMessage(body, res), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** `/api/v2` fetcher for handlers that wrap their payload in `{ success, data, error }`. */
export async function apiV2Envelope<T>(path: string, options: TimedRequestInit = {}): Promise<T> {
  const body = await apiV2Client<{ success?: boolean; data?: T; error?: { message?: string } } | T>(
    path,
    options,
  );
  if (body && typeof body === 'object' && 'data' in body) {
    const env = body as { success?: boolean; data?: T; error?: { message?: string } };
    if (env.success === false) {
      // 502, not 200: the HTTP call succeeded but the envelope reports a
      // server-side failure. Reporting it as 200 made it indistinguishable
      // from success to any caller branching on `status`.
      throw new ApiError(env.error?.message || 'request failed', 502);
    }
    return (env.data ?? (body as T)) as T;
  }
  return body as T;
}

function v2ErrorMessage(body: string, res: Response): string {
  // Proxy/server error pages (nginx 502, Vite proxy failures) are HTML — never surface the markup.
  if (body && !isHtmlErrorBody(body, res.headers?.get('content-type'))) {
    try {
      const parsed = JSON.parse(body) as {
        error?: { message?: string; details?: string };
        reason?: string;
        message?: string;
      };
      const msg = parsed.error?.details || parsed.error?.message || parsed.reason || parsed.message;
      if (msg) return msg;
    } catch {
      /* not JSON — fall through to the raw text */
    }
    return body;
  }
  return httpStatusMessage(res.status);
}

/** Query-key factories shared by hooks and the WebSocket invalidation paths. */
export const amsInboxKey = () => ['ams'] as const;
export const projectAmsKey = (projectId: string) => ['projects', projectId, 'ams'] as const;
export const projectIncidentsKey = (projectId: string) => ['projects', projectId, 'incidents'] as const;
export const understandingInsightsKey = (projectId: string) =>
  ['projects', projectId, 'understanding-insights'] as const;
export const understandingStatusKey = (projectId: string) =>
  ['projects', projectId, 'understanding-status'] as const;
export const skillExecutionsKey = (projectId: string, changeRequestId?: string) =>
  changeRequestId === undefined
    ? (['projects', projectId, 'skill-executions'] as const)
    : (['projects', projectId, 'skill-executions', changeRequestId] as const);
export const workspaceMemoryKey = (projectId: string, ...parts: string[]) =>
  ['projects', projectId, 'workspace-memory', ...parts] as const;
export const knowledgeSnapshotsKey = (projectId: string) =>
  ['projects', projectId, 'knowledge-snapshots'] as const;

interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

interface Meta {
  page?: number;
  page_size?: number;
  total_items?: number;
  total_pages?: number;
  request_id?: string;
  duration_ms?: number;
}

interface WrappedResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  meta?: Meta;
  data_generation?: DataGenerationMeta;
}

export type ApiResult<T> = { data: T; dataGeneration?: DataGenerationMeta };

export interface HealthResponse {
  status: 'healthy' | 'degraded' | 'down';
  version: string;
  uptime: string | number;
  checks?: Record<string, string>;
  workers?: {
    total: number;
    active: number;
    idle: number;
  };
}

/** Backend serves GET /api/v1/health with the same unwrapped JSON body as /health. */
async function fetchHealth(): Promise<HealthResponse> {
  const response = await fetchWithTimeout(`${API_BASE}/health`, {
    headers: authHeaders(),
  });
  handleUnauthorizedResponse(response.status, '/health');
  if (!response.ok) {
    throw new ApiError(`Health check failed: ${response.status}`, response.status);
  }
  const raw = (await response.json()) as Record<string, unknown>;
  return {
    status: (raw.status as HealthResponse['status']) ?? 'down',
    version: String(raw.version ?? ''),
    uptime: (raw.uptime as string | number) ?? 0,
    checks: raw.checks as Record<string, string> | undefined,
    workers: (raw.workers as HealthResponse['workers']) ?? { total: 0, active: 0, idle: 0 },
  };
}

// Demo mode disabled - use real API
const DEMO_MODE = false;

/**
 * Optional service API key for automation builds. There is no built-in
 * development key any more: operators sign in (Google or dev-login) and the
 * session JWT authenticates every request, including WebSockets and browser
 * downloads via {@link wsCredentialQuery} / {@link downloadCredentialQuery}.
 */
export const ENSOMIRAI_API_KEY =
  import.meta.env.VITE_ENSOMIRAI_API_KEY || import.meta.env.VITE_API_KEY || '';

/**
 * Query-string credential for the few routes a browser cannot send headers
 * to (WebSocket handshakes, anchor downloads). Prefers the signed-in
 * session token; falls back to a configured service key. The backend
 * accepts these only on an explicit route allow-list and redacts them from
 * logs and traces.
 */
export function credentialQueryParams(): URLSearchParams {
  const params = new URLSearchParams();
  let token = '';
  try {
    token = (localStorage.getItem('enso.auth.token') || '').trim();
  } catch {
    token = '';
  }
  if (token) {
    params.set('access_token', token);
  } else if (ENSOMIRAI_API_KEY) {
    params.set('api_key', ENSOMIRAI_API_KEY);
  }
  return params;
}

/** `?access_token=…` / `?api_key=…` / '' — for WebSocket URLs. */
export function wsCredentialQuery(): string {
  const qs = credentialQueryParams().toString();
  return qs ? `?${qs}` : '';
}

/** `&access_token=…` / `&api_key=…` / '' — appended to a URL that already has a query. */
export function downloadCredentialQuery(): string {
  const qs = credentialQueryParams().toString();
  return qs ? `&${qs}` : '';
}

/** Must match api.schema_codec.hmac_secret / ENSOMIRAI_SCHEMA_HMAC_SECRET */
export const ENSOMIRAI_SCHEMA_HMAC_SECRET =
  import.meta.env.VITE_SCHEMA_HMAC_SECRET || '';

const SCHEMA_CODEC_ENABLED =
  import.meta.env.VITE_SCHEMA_CODEC_ENABLED === 'true';

function apiKeyHeader(): Record<string, string> {
  return ENSOMIRAI_API_KEY ? { 'X-API-Key': ENSOMIRAI_API_KEY } : {};
}

function sessionAuthHeader(): Record<string, string> {
  try {
    const token = (localStorage.getItem('enso.auth.token') || '').trim();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Actor email for project Team & Roles RBAC (X-Actor-Email). */
function actorEmailHeader(): Record<string, string> {
  try {
    const email = (localStorage.getItem('enso.identity.email') || '').trim().toLowerCase();
    return email ? { 'X-Actor-Email': email } : {};
  } catch {
    return {};
  }
}

/**
 * Credential headers for every API call. The signed-in session JWT always
 * wins; the optional automation key ({@link ENSOMIRAI_API_KEY}) is only sent
 * when there is no session, because the API would otherwise treat the caller
 * as the service account and list every project.
 */
export function authHeaders(): Record<string, string> {
  const session = sessionAuthHeader();
  if (session.Authorization) {
    return { ...session, ...actorEmailHeader() };
  }
  return { ...apiKeyHeader(), ...actorEmailHeader() };
}

function isSchemaEnvelope(raw: unknown): raw is SchemaEnvelope {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'payload' in raw &&
    typeof (raw as SchemaEnvelope).payload === 'string'
  );
}

// API Client
export async function apiClient<T>(
  endpoint: string,
  options: TimedRequestInit = {}
): Promise<T> {
  if (DEMO_MODE) {
    throw new Error('Demo mode - using local state');
  }

  const method = (options.method || 'GET').toUpperCase();
  const useCodec = SCHEMA_CODEC_ENABLED && !!ENSOMIRAI_SCHEMA_HMAC_SECRET;

  let body = options.body;
  const headers: Record<string, string> = {
    ...authHeaders(),
    ...(options.headers as Record<string, string>),
  };

  if (useCodec) {
    // GET: only ask for encoded responses. POST/PUT/PATCH: send signed base64 body.
    headers.Accept = SCHEMA_CODEC_HEADERS.accept.Accept;
    if (body && typeof body === 'string' && method !== 'GET' && method !== 'HEAD') {
      const parsed = JSON.parse(body) as unknown;
      const env = await encodeSchemaEnvelope(parsed, ENSOMIRAI_SCHEMA_HMAC_SECRET);
      body = JSON.stringify(env);
      headers['Content-Type'] = SCHEMA_CODEC_HEADERS.request['Content-Type'];
      headers['X-Enso-Schema-Codec'] = SCHEMA_CODEC_HEADERS.request['X-Enso-Schema-Codec'];
    }
  } else if (!headers['Content-Type'] && body) {
    headers['Content-Type'] = 'application/json';
  }

  const { timeoutMs, ...fetchOptions } = options;
  const response = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    method,
    body,
    headers,
    timeoutMs,
  });
  handleUnauthorizedResponse(response.status, endpoint);

  let parsed: unknown = await response.json().catch(() => ({
    success: false,
    error: {
      code: 'parse_error',
      message: response.ok ? 'The server returned an unreadable response.' : httpStatusMessage(response.status),
    },
  }));

  if (useCodec && isSchemaEnvelope(parsed)) {
    try {
      parsed = await decodeSchemaEnvelope<WrappedResponse<T>>(parsed, ENSOMIRAI_SCHEMA_HMAC_SECRET);
    } catch (e) {
      throw new Error(e instanceof Error ? e.message : 'schema response decode failed');
    }
  }

  const wrapped = parsed as WrappedResponse<T>;

  if (!response.ok) {
    const details = wrapped.error?.details || wrapped.error?.message;
    throw new ApiError(details || httpStatusMessage(response.status), response.status, wrapped.error?.code);
  }
  if (wrapped.success === false) {
    const details = wrapped.error?.details || wrapped.error?.message;
    throw new ApiError(details || 'API request failed', response.status, wrapped.error?.code);
  }

  return wrapped.data;
}

/** Like apiClient but preserves Wave D `data_generation` metadata. */
export async function apiClientWithMeta<T>(
  endpoint: string,
  options: TimedRequestInit = {},
): Promise<ApiResult<T>> {
  if (DEMO_MODE) {
    throw new Error('Demo mode - using local state');
  }

  const method = (options.method || 'GET').toUpperCase();
  const useCodec = SCHEMA_CODEC_ENABLED && !!ENSOMIRAI_SCHEMA_HMAC_SECRET;

  let body = options.body;
  const headers: Record<string, string> = {
    ...authHeaders(),
    ...(options.headers as Record<string, string>),
  };

  if (useCodec) {
    headers.Accept = SCHEMA_CODEC_HEADERS.accept.Accept;
    if (body && typeof body === 'string' && method !== 'GET' && method !== 'HEAD') {
      const parsed = JSON.parse(body) as unknown;
      const env = await encodeSchemaEnvelope(parsed, ENSOMIRAI_SCHEMA_HMAC_SECRET);
      body = JSON.stringify(env);
      headers['Content-Type'] = SCHEMA_CODEC_HEADERS.request['Content-Type'];
      headers['X-Enso-Schema-Codec'] = SCHEMA_CODEC_HEADERS.request['X-Enso-Schema-Codec'];
    }
  } else if (!headers['Content-Type'] && body) {
    headers['Content-Type'] = 'application/json';
  }

  const { timeoutMs, ...fetchOptions } = options;
  const response = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    method,
    body,
    headers,
    timeoutMs,
  });
  handleUnauthorizedResponse(response.status, endpoint);

  let parsed: unknown = await response.json().catch(() => ({
    success: false,
    error: {
      code: 'parse_error',
      message: response.ok ? 'The server returned an unreadable response.' : httpStatusMessage(response.status),
    },
  }));

  if (useCodec && isSchemaEnvelope(parsed)) {
    try {
      parsed = await decodeSchemaEnvelope<WrappedResponse<T>>(parsed, ENSOMIRAI_SCHEMA_HMAC_SECRET);
    } catch (e) {
      throw new Error(e instanceof Error ? e.message : 'schema response decode failed');
    }
  }

  const wrapped = parsed as WrappedResponse<T>;

  if (!response.ok) {
    const details = wrapped.error?.details || wrapped.error?.message;
    throw new ApiError(details || httpStatusMessage(response.status), response.status, wrapped.error?.code);
  }
  if (wrapped.success === false) {
    const details = wrapped.error?.details || wrapped.error?.message;
    throw new ApiError(details || 'API request failed', response.status, wrapped.error?.code);
  }

  return { data: wrapped.data, dataGeneration: wrapped.data_generation };
}

/** Public version of auth headers so non-JSON consumers (blob downloads) can authenticate. */
export function buildApiHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return { ...authHeaders(), ...extra };
}

/**
 * Authenticated binary download from `/api/v1` — used by the export buttons.
 * Triggers the browser save dialog and resolves once the blob is handed off.
 */
export async function downloadApiBlob(endpoint: string, filename: string): Promise<void> {
  const res = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: buildApiHeaders(),
    timeoutMs: 120_000,
  });
  handleUnauthorizedResponse(res.status, endpoint);
  if (!res.ok) {
    let message = `Download failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string; details?: string } };
      message = body.error?.details || body.error?.message || message;
    } catch {
      // body wasn't JSON — keep the default message
    }
    throw new ApiError(message, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PHASE0_VERIFY_ENDPOINTS: { id: string; label: string; path: (projectId: string) => string }[] = [
  {
    id: 'scanner-inventory',
    label: 'Scanner inventory (Task 0.2 — entities, relations, provenance)',
    path: (id) => `/projects/${id}/scanner/inventory`,
  },
  {
    id: 'intake-prepared',
    label: 'Intake prepared repos (Task 0.1)',
    path: (id) => `/projects/${id}/intake/prepared`,
  },
  {
    id: 'ingestion-summary',
    label: 'Ingestion summary',
    path: (id) => `/projects/${id}/ingestion/summary`,
  },
  {
    id: 'phase0-summary',
    label: 'Phase 0 inventory counts (DB)',
    path: (id) => `/projects/${id}/phase0/summary`,
  },
  {
    id: 'phase0-services',
    label: 'Services (persisted)',
    path: (id) => `/projects/${id}/phase0/services`,
  },
  {
    id: 'phase0-components',
    label: 'Components (persisted)',
    path: (id) => `/projects/${id}/phase0/components`,
  },
  {
    id: 'phase0-api-endpoints',
    label: 'API endpoints (persisted)',
    path: (id) => `/projects/${id}/phase0/api-endpoints`,
  },
  {
    id: 'phase0-dependencies',
    label: 'Dependencies (persisted)',
    path: (id) => `/projects/${id}/phase0/dependencies`,
  },
  {
    id: 'knowledge-graph',
    label: 'Knowledge graph (nodes + edges)',
    path: (id) => `/projects/${id}/knowledge/graph`,
  },
  {
    id: 'kg-stats',
    label: 'Knowledge graph stats',
    path: (id) => `/projects/${id}/knowledge/stats`,
  },
  {
    id: 'understanding-insights',
    label: 'Understanding worker insights',
    path: (id) => `/projects/${id}/understanding/insights`,
  },
  {
    id: 'reporting-metrics',
    label: 'Phase 0 reporting metrics',
    path: (id) => `/projects/${id}/reporting/metrics`,
  },
];

function parseIngestionPhase(raw: unknown): Project['ingestionPhase'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.phase !== 'string' || !r.phase) return undefined;
  return {
    phase: r.phase,
    done: Number(r.done) || 0,
    total: Number(r.total) || 0,
    at: typeof r.at === 'string' ? r.at : undefined,
  };
}

// Map backend snake_case to frontend camelCase
function mapBackendProject(raw: Record<string, unknown>): Project {
  return sanitizeProject({
    id: String(raw.id ?? ''),
    name: raw.name as string,
    sourceLanguage: (raw.source_language as string) || '',
    targetLanguage: (raw.target_language as string) || '',
    status: normalizeProjectStatus(raw.status),
    progress: (raw.progress as number) || 0,
    createdAt: (raw.created_at as string) || undefined,
    updatedAt: (raw.updated_at as string) || undefined,
    worker: (raw.worker as string) || '',
    ingestionStatus: (raw.ingestion_status as string) || undefined,
    ingestionError: (raw.ingestion_error as string) || undefined,
    ingestionPhase: parseIngestionPhase(raw.ingestion_phase),
  });
}

// Map frontend camelCase to backend snake_case for requests
function mapToBackendProject(project: Partial<Project>): Record<string, unknown> {
  return {
    name: project.name,
    source_language: project.sourceLanguage,
    target_language: project.targetLanguage,
    progress: project.progress,
    worker: project.worker,
  };
}

// Health Check Hook — no silent fallback when API is down
export function useHealthCheck() {
  return useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    refetchInterval: () => pollIntervalMs(30_000),
    staleTime: 10_000,
    retry: 1,
  });
}

export function useApiConnected() {
  const { data, isError, isLoading } = useHealthCheck();
  const reachable = data?.status === 'healthy' || data?.status === 'degraded';
  return {
    connected: !isLoading && !isError && reachable,
    health: data,
    isLoading,
    isError,
  };
}

const optNum = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const optStr = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

function mapPlatformWorkerStats(raw: unknown): PlatformWorkerStats | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  return {
    totalRuns: optNum(s.total_runs) ?? 0,
    succeeded: optNum(s.succeeded) ?? 0,
    failed: optNum(s.failed) ?? 0,
    active: optNum(s.active) ?? 0,
    stalled: optNum(s.stalled) ?? 0,
    waiting: optNum(s.waiting) ?? 0,
    runs24h: optNum(s.runs_24h) ?? 0,
    projects: optNum(s.projects) ?? 0,
    successRate: optNum(s.success_rate),
    avgDurationMs: optNum(s.avg_duration_ms),
    maxDurationMs: optNum(s.max_duration_ms),
    firstRunAt: optStr(s.first_run_at),
    lastRunAt: optStr(s.last_run_at),
    lastSuccessAt: optStr(s.last_success_at),
    lastFailureAt: optStr(s.last_failure_at),
    latestStatus: optStr(s.latest_status),
    lastError: optStr(s.last_error),
  };
}

function mapPlatformWorker(raw: Record<string, unknown>): PlatformWorker {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? raw.type ?? 'Worker'),
    type: raw.type as string | undefined,
    status: (raw.status as PlatformWorker['status']) || 'idle',
    description: raw.description as string | undefined,
    layer: optStr(raw.layer),
    pipeline: optStr(raw.pipeline),
    taskType: optStr(raw.task_type),
    diagramKind: optStr(raw.diagram_kind),
    stats: mapPlatformWorkerStats(raw.stats),
    activity: Array.isArray(raw.activity)
      ? (raw.activity as Record<string, unknown>[]).map((d) => ({
          date: String(d.date ?? ''),
          total: optNum(d.total) ?? 0,
          succeeded: optNum(d.succeeded) ?? 0,
          failed: optNum(d.failed) ?? 0,
        }))
      : undefined,
  };
}

function mapPlatformWorkerRun(raw: Record<string, unknown>): PlatformWorkerRun {
  return {
    id: String(raw.id ?? ''),
    projectId: optStr(raw.project_id),
    projectName: optStr(raw.project_name),
    name: optStr(raw.name),
    status: String(raw.status ?? ''),
    error: optStr(raw.error),
    createdAt: String(raw.created_at ?? ''),
    updatedAt: optStr(raw.updated_at),
    startedAt: optStr(raw.started_at),
    completedAt: optStr(raw.completed_at),
    durationMs: optNum(raw.duration_ms),
    elapsedMs: optNum(raw.elapsed_ms),
    stalled: raw.stalled === true,
    insightsCount: optNum(raw.insights_count),
  };
}

// Workers Hooks — GET /api/v1/workers?project_id= (8 understanding workers + scanner)
export function useWorkers(projectId?: string) {
  const query = useQuery({
    queryKey: ['workers', projectId ?? 'global'],
    queryFn: async () => {
      const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
      const response = await apiClient<{ workers?: Record<string, unknown>[]; total?: number }>(
        `/workers${qs}`
      );
      return (response.workers ?? []).map(mapPlatformWorker).filter((w) => w.id);
    },
    staleTime: 30_000,
    refetchInterval: (query) =>
      refetchWhenActive(query, {
        intervalMs: projectId ? 5_000 : 60_000,
        shouldPoll: (workers) =>
          !projectId ||
          (workers ?? []).some((w) => ['busy', 'waiting', 'running', 'pending'].includes(w.status)),
      }),
  });

  return {
    workers: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
    isFetching: query.isFetching,
  };
}

export interface SchedulerHealth {
  enabled: boolean;
  running: boolean;
  last_tick_at: string;
  seconds_since_last_tick: number;
  last_tick_duration_ms: number;
  last_tick_error?: string;
  ticks_completed: number;
  triggers_retried_last_tick: number;
  runs_claimed_last_tick: number;
  triggers_retried_total: number;
  runs_claimed_total: number;
  tick_interval_seconds: number;
  stale_after_seconds: number;
}

// Ops Hooks — GET /api/v1/ops/scheduler (Engineering Investigation resume-sweep health)
export function useSchedulerHealth() {
  const query = useQuery({
    queryKey: ['ops', 'scheduler'],
    queryFn: async () => {
      const response = await apiClient<{ data?: SchedulerHealth }>('/ops/scheduler');
      return response.data ?? null;
    },
    staleTime: 20_000,
    refetchInterval: () => pollIntervalMs(30_000),
  });

  return {
    health: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export type WorkerRunStatusFilter = '' | 'succeeded' | 'failed' | 'active' | 'waiting';

export interface WorkerDetailOptions {
  projectId?: string;
  status?: WorkerRunStatusFilter;
  limit?: number;
  offset?: number;
  days?: number;
}

// GET /api/v1/workers/:id — descriptor, run stats, daily activity and run history.
export function useWorker(workerId: string, options: WorkerDetailOptions = {}) {
  const { projectId, status = '', limit = 20, offset = 0, days = 30 } = options;
  const query = useQuery({
    queryKey: ['workers', 'detail', workerId, projectId ?? 'global', status, limit, offset, days],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset), days: String(days) });
      if (projectId) params.set('project_id', projectId);
      if (status) params.set('status', status);
      try {
        const response = await apiClient<{
          worker?: Record<string, unknown>;
          runs?: Record<string, unknown>[];
          runs_total?: number;
          stats_available?: boolean;
        }>(`/workers/${encodeURIComponent(workerId)}?${params}`);
        return {
          worker: response.worker ? mapPlatformWorker(response.worker) : null,
          runs: (response.runs ?? []).map(mapPlatformWorkerRun),
          runsTotal: response.runs_total ?? 0,
          statsAvailable: response.stats_available !== false,
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { worker: null, runs: [], runsTotal: 0, statsAvailable: false };
        }
        throw err;
      }
    },
    enabled: !!workerId,
    placeholderData: (prev) => prev,
    refetchInterval: (q) =>
      refetchWhenActive(q, {
        intervalMs: 10_000,
        shouldPoll: (data) => (data?.worker?.stats?.active ?? 0) > 0 || (data?.worker?.stats?.waiting ?? 0) > 0,
      }),
  });

  return {
    worker: query.data?.worker ?? undefined,
    runs: query.data?.runs ?? [],
    runsTotal: query.data?.runsTotal ?? 0,
    statsAvailable: query.data?.statsAvailable ?? true,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// Projects Hooks
export function useProjects() {
  const query = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const pageSize = 100;
      let page = 1;
      const all: ReturnType<typeof mapBackendProject>[] = [];
      let totalItems = Number.POSITIVE_INFINITY;

      while (all.length < totalItems && page <= 50) {
        const wrapped = await fetch(`${API_BASE}/projects?page=${page}&page_size=${pageSize}`, {
          headers: {
            Accept: 'application/json',
            ...authHeaders(),
          },
        }).then(async (res) => {
          handleUnauthorizedResponse(res.status, '/projects');
          const json = (await res.json().catch(() => ({}))) as WrappedResponse<
            Record<string, unknown>[] | { projects?: Record<string, unknown>[] }
          > & { meta?: Meta };
          if (!res.ok || json.success === false) {
            throw new ApiError(json.error?.message || `API Error: ${res.status}`, res.status, json.error?.code);
          }
          return json;
        });

        const raw = wrapped.data;
        const rows = Array.isArray(raw) ? raw : raw?.projects ?? [];
        for (const row of rows) {
          const mapped = mapBackendProject(row);
          if (mapped.id) all.push(mapped);
        }

        const metaTotal = Number(wrapped.meta?.total_items);
        totalItems = Number.isFinite(metaTotal) && metaTotal >= 0 ? metaTotal : all.length;
        if (rows.length === 0) break;
        page += 1;
      }

      return all;
    },
    refetchOnWindowFocus: true,
    retry: 2,
    refetchInterval: () => pollIntervalMs(30_000),
  });

  return {
    projects: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface PlatformUniverseNode {
  id: string;
  name: string;
  status?: string;
  applications?: string[];
  open_incidents?: number;
  cluster_id?: string;
  cluster_label?: string;
  show_label?: boolean;
  primary_app?: string;
  x?: number;
  y?: number;
}

export interface PlatformUniverseEdge {
  id: string;
  source: string;
  target: string;
  kind: string;
  label: string;
  weight: number;
  evidence?: string[];
}

export interface PlatformUniverseResponse {
  nodes: PlatformUniverseNode[];
  edges: PlatformUniverseEdge[];
  applications?: Array<{
    key: string;
    label: string;
    project_ids: string[];
    edge_count?: number;
    kind?: string;
  }>;
  insight?: {
    summary?: string;
    clusters?: Array<{ id: string; label: string; project_ids: string[]; why?: string }>;
    focus_edge_ids?: string[];
    label_node_ids?: string[];
  };
  llm?: { provider?: string; model?: string; status?: string; detail?: string };
  generated_at?: string;
  sources?: string[];
}

export function usePlatformUniverse(options?: { enrich?: boolean }) {
  const enrich = options?.enrich !== false;
  return useQuery({
    queryKey: ['platform', 'universe', enrich],
    queryFn: () =>
      apiClient<PlatformUniverseResponse>(`/platform/universe?enrich=${enrich ? 'true' : 'false'}`, {
        timeoutMs: 25_000,
      }),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const status = query.state.data?.llm?.status;
      // Poll faster while Claude insight is still building.
      if (enrich && (status === 'pending' || !status)) {
        return pollIntervalMs(8_000);
      }
      return pollIntervalMs(90_000);
    },
    retry: 1,
  });
}

export function useProject(projectId: string) {
  const query = useQuery({
    queryKey: ['projects', projectId],
    queryFn: async () => {
      const response = await apiClient<Record<string, unknown>>(`/projects/${projectId}`);
      return mapBackendProject(response);
    },
    enabled: !!projectId,
    staleTime: 60_000,
    refetchInterval: (query) =>
      refetchWhenActive(query, {
        intervalMs: 3_000,
        shouldPoll: (project) =>
          shouldPollProjectDuringIngestion(project) || shouldShowIngestionGate(project, undefined, undefined, projectId),
      }),
    retry: (count, error) => {
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('not found'))) {
        return false;
      }
      return count < 2;
    },
  });

  return {
    project: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isNotFound:
      query.isError &&
      query.error instanceof Error &&
      (query.error.message.includes('404') || query.error.message.includes('not found')),
  };
}

export interface RepoSpec {
  source_type?: 'git' | 'zip' | 'tarball' | 'local';
  url: string;
  branch?: string;
  local_path?: string;
  /** HTTPS private repos — GitHub: PAT or x-access-token:TOKEN; Bitbucket: username:app_password */
  auth_token?: string;
  git_connection_id?: string;
}

export interface IntakeRisk {
  risk_type: string;
  severity: string;
  title: string;
  file_path?: string;
  line?: number;
  details?: string;
}

export interface IntakeDeltaEntry {
  file_path: string;
  action: string;
  reason: string;
  old_hash?: string;
  new_hash?: string;
}

export interface PreparedRepository {
  id: string;
  project_id: string;
  repository_id: string;
  working_path: string;
  validation_ok: boolean;
  validation_message?: string;
  manifest: {
    prepared_id: string;
    source_uri: string;
    source_type: string;
    input_sha256: string;
    total_files: number;
    total_bytes: number;
    has_vcs: boolean;
    has_manifest: boolean;
  };
  delta: {
    has_prior_scan: boolean;
    added: IntakeDeltaEntry[];
    modified: IntakeDeltaEntry[];
    deleted: IntakeDeltaEntry[];
  };
  risks: IntakeRisk[];
}

export interface CreateProjectInput extends Omit<Project, 'id' | 'createdAt' | 'updatedAt'> {
  /** Canonical: one repository per project. */
  repository?: RepoSpec;
  /** Compatibility: at most one element is accepted by the API. */
  repositories?: RepoSpec[];
  /** Set when archives will be uploaded immediately after create (no git URLs yet). */
  pending_archive?: boolean;
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newProject: CreateProjectInput) => {
      const raw = newProject.repository
        ? [newProject.repository]
        : newProject.repositories ?? [];
      const repos = normalizeRepoSpecs(
        raw
          .filter((r) => r.url.trim())
          .slice(0, 1)
          .map(({ url, branch, source_type, auth_token, git_connection_id }) => ({
            url: url.trim(),
            branch: branch?.trim() || 'main',
            source_type: source_type || 'git',
            ...(auth_token?.trim() ? { auth_token: auth_token.trim() } : {}),
            ...(git_connection_id?.trim() ? { git_connection_id: git_connection_id.trim() } : {}),
          })),
      );
      const backendData = {
        ...mapToBackendProject(newProject),
        repository: repos[0],
        repositories: repos,
        pending_archive: Boolean(newProject.pending_archive),
      };
      const response = await apiClient<Record<string, unknown>>('/projects', {
        method: 'POST',
        body: JSON.stringify(backendData),
      });
      return mapBackendProject(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: true });
    },
  });
}

export interface SourceFileRecord {
  id: string;
  projectId: string;
  repositoryId: string;
  filePath: string;
  fileHash: string;
  language: string;
  linesOfCode: number;
  complexityScore: number;
  technicalDebtMinutes: number;
}

export interface SourceFileContentResponse {
  file_path: string;
  content: string;
  encoding: 'base64' | 'plain' | 'none';
  note?: string;
}

export interface IngestionSummary {
  file_count: number;
  total_loc: number;
  repository_count: number;
  by_language: Record<string, number>;
}

export interface RepositoryRecord {
  id: string;
  projectId: string;
  url: string;
  branch: string;
  sourceType: string;
  /** Uploaded archive of a multi-repository project: the key its retries use. */
  repositoryKey?: string;
  /** queued | preparing | scanning | completed | failed; absent for single-repository projects. */
  ingestionStatus?: string;
  ingestionError?: string;
  archiveName?: string;
}

export interface TaskRecord {
  id: string;
  project_id: string;
  name: string;
  description: string;
  type: string;
  status: string;
  priority: number;
  worker_id?: string;
  config?: unknown;
  result?: unknown;
  error?: string;
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
}

export interface Phase0Summary {
  services: number;
  applications?: number;
  datastores: number;
  components: number;
  api_endpoints: number;
  documents: number;
  dependencies: number;
  behavior_relationships?: number;
  data_flow_relationships?: number;
  artifacts: number;
  service_relationships: number;
  service_datastore_links: number;
  file_count?: number;
}

export interface RepositoryArtifact {
  id: string;
  action: string;
  file_path: string;
  reason: string;
  created_at: string;
}

function mapSourceFile(raw: Record<string, unknown>): SourceFileRecord {
  return {
    id: raw.id as string,
    projectId: raw.project_id as string,
    repositoryId: raw.repository_id as string,
    filePath: raw.file_path as string,
    fileHash: raw.file_hash as string,
    language: raw.language as string,
    linesOfCode: (raw.lines_of_code as number) || 0,
    complexityScore: (raw.complexity_score as number) || 0,
    technicalDebtMinutes: (raw.technical_debt_minutes as number) || 0,
  };
}

function mapRepository(raw: Record<string, unknown>): RepositoryRecord {
  return {
    id: raw.id as string,
    projectId: raw.project_id as string,
    url: raw.url as string,
    branch: (raw.branch as string) || 'main',
    sourceType: (raw.source_type as string) || 'git',
    repositoryKey: (raw.repository_key as string) || undefined,
    ingestionStatus: (raw.ingestion_status as string) || undefined,
    ingestionError: (raw.ingestion_error as string) || undefined,
    archiveName: (raw.archive_name as string) || undefined,
  };
}

/** Removes one uploaded-archive repository of a multi-repository project. */
export function useRemoveProjectRepository(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (repositoryId: string) =>
      apiClient<{ removed: string; ready: boolean }>(`/projects/${projectId}/repositories/${repositoryId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'repositories'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ingestion-summary'] });
    },
  });
}

/** Uploads a replacement for one failed repository under its own key. */
export function useRetryProjectRepository(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, repositoryKey }: { file: File; repositoryKey: string }) =>
      uploadProjectArchive(projectId, file, { repositoryKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'repositories'] });
    },
  });
}

export function useProjectSourceFiles(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  return useQuery({
    queryKey: ['projects', projectId, 'source-files'],
    queryFn: async () => {
      const rows = await apiClient<Record<string, unknown>[] | null>(
        `/projects/${projectId}/source-files`
      );
      return (rows ?? []).map(mapSourceFile);
    },
    enabled: !!projectId,
    staleTime: poll ? 5_000 : 120_000,
    refetchInterval: (query) =>
      poll
        ? refetchWhenActive(query, { intervalMs: 5_000 })
        : false,
  });
}

export function useSourceFileContent(projectId: string, fileId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['projects', projectId, 'source-files', fileId, 'content'],
    queryFn: ({ signal }) =>
      apiClient<SourceFileContentResponse>(`/projects/${projectId}/source-files/${fileId}/content`, { signal }),
    enabled: !!projectId && !!fileId && enabled,
  });
}

/** Phase-0 intake still running — same signal the project query polls on (failed ingests excluded). */
function ingestionActive(project: Project | undefined, projectId: string): boolean {
  if (!project || isIngestionFailed(project)) return false;
  return shouldPollProjectDuringIngestion(project) || shouldShowIngestionGate(project, undefined, undefined, projectId);
}

/**
 * Repository intake counts. Polls only while ingestion is running (per the
 * project record); `poll: false` opts out entirely and `fastPoll` picks the
 * 1s cadence. When a run finishes, one final refetch picks up the end counts.
 */
export function useIngestionSummary(projectId: string, options?: { fastPoll?: boolean; poll?: boolean }) {
  const { project } = useProject(projectId);
  const running = ingestionActive(project, projectId);
  const poll = (options?.poll ?? true) && running;
  const interval = options?.fastPoll ? 1_000 : 3_000;
  const queryKey = ['projects', projectId, 'ingestion-summary'];
  const qc = useQueryClient();
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running && projectId) {
      void qc.invalidateQueries({ queryKey: ['projects', projectId, 'ingestion-summary'] });
    }
    wasRunning.current = running;
  }, [running, projectId, qc]);
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => apiClient<IngestionSummary>(`/projects/${projectId}/ingestion/summary`, { signal }),
    enabled: !!projectId,
    staleTime: poll ? 3_000 : 120_000,
    refetchInterval: poll ? () => pollIntervalMs(interval) : false,
  });
}

export interface DiscoveredApplication {
  name: string;
  root_path: string;
  kind: string;
  manifest: string;
  confidence: number;
}

export function useProjectApplications(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? false;
  return useQuery({
    queryKey: ['projects', projectId, 'applications'],
    queryFn: async () => {
      const data = await apiClient<{ applications?: DiscoveredApplication[]; count?: number }>(
        `/projects/${projectId}/applications`,
      );
      return {
        applications: data?.applications ?? [],
        count: data?.count ?? data?.applications?.length ?? 0,
      };
    },
    enabled: Boolean(projectId),
    staleTime: poll ? 5_000 : 60_000,
    refetchInterval: poll ? () => pollIntervalMs(5_000) : false,
  });
}

export type WorkflowPhaseId = 'discover' | 'understand' | 'change' | 'operate';

export type WorkflowPhaseStatus =
  | 'locked'
  | 'available'
  | 'active'
  | 'running'
  | 'complete'
  | 'partial';

export interface ProjectWorkflowPhase {
  id: WorkflowPhaseId;
  label: string;
  status: WorkflowPhaseStatus;
  progress: number;
  summary?: string;
}

export interface ProjectWorkflowResponse {
  project_id: string;
  current_phase: WorkflowPhaseId;
  phases: ProjectWorkflowPhase[];
  completed_phases: WorkflowPhaseId[];
  active_workers: Array<{
    id: string;
    name: string;
    layer?: string;
    status: string;
    task_id?: string;
    phase: WorkflowPhaseId;
  }>;
  capabilities: Array<{ id: string; label: string; ready: boolean }>;
  blocked_capabilities: Array<{
    id: string;
    label: string;
    reason: string;
    missing_dependency?: string;
    enable_hint?: string;
  }>;
  next_action: {
    label: string;
    phase: WorkflowPhaseId;
    path: string;
    api_path?: string;
  };
  progress: {
    overall: number;
    by_phase: Record<string, number>;
    milestones_hit: string[];
  };
  readiness: Array<{ id: string; ready: boolean; reason?: string }>;
  recent_events?: Array<{
    type: string;
    label: string;
    occurred_at?: string;
    description?: string;
  }>;
  meta: { generated_at: string; source: string; duration_ms: number };
}

export function useProjectWorkflow(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'workflow'],
    queryFn: async () => {
      try {
        return await apiV2Client<ProjectWorkflowResponse>(`/projects/${projectId}/workflow`);
      } catch (e) {
        if (e instanceof Error && /404|not found/i.test(e.message)) {
          return null;
        }
        throw e;
      }
    },
    enabled: !!projectId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      if (query.state.data === null && query.state.fetchStatus === 'idle') {
        return false;
      }
      return refetchWhenActive(query, {
        intervalMs: workflowNeedsFastPoll(query.state.data ?? undefined) ? 10_000 : 30_000,
      });
    },
    retry: (_, error) => !(error instanceof Error && /404|not found/i.test(error.message)),
  });
}

export interface EntityLink {
  rel: string;
  entity_id: string;
  entity_type: string;
  label?: string;
}

export interface EntityInspectorResponse {
  entity: {
    id: string;
    type: string;
    display_name: string;
    status?: string;
    project_id?: string;
    metadata?: Record<string, unknown>;
    links?: EntityLink[];
    origin?: { system: string; source?: string; graph_id?: string };
    timestamps?: { created_at?: string; updated_at?: string };
  };
  relationships: Array<{
    direction: string;
    relation: string;
    entity_id: string;
    entity_type: string;
    label: string;
    confidence?: number;
  }>;
  linked: {
    investigations?: EntityLink[];
    repositories?: EntityLink[];
    documentation?: EntityLink[];
    incidents?: EntityLink[];
    changes?: EntityLink[];
    evidence?: EntityLink[];
    services?: EntityLink[];
  };
  available_actions: Array<{
    id: string;
    label: string;
    description?: string;
    http_method?: string;
    http_path?: string;
    requires?: string;
  }>;
  meta: { generated_at: string; source: string; duration_ms: number };
}

export async function fetchEntityInspect(
  entityId: string,
  projectId: string,
): Promise<EntityInspectorResponse> {
  return apiV2Client(
    `/entities/${encodeURIComponent(entityId)}?project_id=${encodeURIComponent(projectId)}`,
  );
}

export function useEntityInspect(entityId: string | undefined, projectId: string) {
  return useQuery({
    queryKey: ['entities', entityId, projectId],
    queryFn: () => fetchEntityInspect(entityId!, projectId),
    enabled: !!entityId && !!projectId,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useProjectRepositories(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'repositories'],
    queryFn: async () => {
      const rows = await apiClient<Record<string, unknown>[]>(
        `/projects/${projectId}/repositories`
      );
      return rows.map(mapRepository);
    },
    enabled: !!projectId,
  });
}

function mapTaskRecord(raw: Record<string, unknown>): TaskRecord {
  const id = (raw.id ?? raw.ID) as string;
  return {
    id,
    project_id: (raw.project_id ?? raw.ProjectID) as string,
    name: (raw.name ?? raw.Name) as string,
    description: (raw.description ?? raw.Description) as string,
    type: (raw.type ?? raw.Type) as string,
    status: (raw.status ?? raw.Status) as string,
    priority: (raw.priority ?? raw.Priority) as number,
    worker_id: (raw.worker_id ?? raw.WorkerID) as string | undefined,
    config: raw.config ?? raw.Config,
    result: raw.result ?? raw.Result,
    error: (raw.error ?? raw.Error) as string | undefined,
    created_at: (raw.created_at ?? raw.CreatedAt) as string | undefined,
    updated_at: (raw.updated_at ?? raw.UpdatedAt) as string | undefined,
    started_at: (raw.started_at ?? raw.StartedAt) as string | undefined,
    completed_at: (raw.completed_at ?? raw.CompletedAt) as string | undefined,
  };
}

export function useProjectTasks(projectId: string, type?: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  return useQuery({
    queryKey: ['projects', projectId, 'tasks', type],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('project_id', projectId);
      if (type) qs.set('type', type);
      const response = await apiClient<{ tasks?: Record<string, unknown>[] }>(`/tasks?${qs.toString()}`);
      return (response.tasks ?? []).map(mapTaskRecord);
    },
    enabled: !!projectId,
    staleTime: poll ? 3_000 : 120_000,
    refetchInterval: (query) =>
      poll
        ? refetchWhenActive(query, {
            intervalMs: 3_000,
            shouldPoll: (tasks) => tasksStillRunning(tasks),
          })
        : false,
  });
}

export function usePhase0Summary(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  return useQuery({
    queryKey: ['projects', projectId, 'phase0-summary'],
    queryFn: ({ signal }) => apiClient<Phase0Summary>(`/projects/${projectId}/phase0/summary`, { signal }),
    enabled: !!projectId,
    staleTime: poll ? 5_000 : 120_000,
    refetchInterval: poll ? () => pollIntervalMs(5_000) : false,
  });
}

export function usePhase0Artifacts(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'phase0-artifacts'],
    queryFn: ({ signal }) => apiClient<RepositoryArtifact[]>(`/projects/${projectId}/phase0/artifacts`, { signal }),
    enabled: !!projectId,
  });
}

export type Phase0InventoryKind =
  | 'services'
  | 'components'
  | 'api-endpoints'
  | 'datastores'
  | 'documents'
  | 'dependencies'
  | 'artifacts'
  | 'service-relationships'
  | 'service-datastore-links';

export interface Phase0ServiceItem {
  id: string;
  name: string;
  service_type: string;
  root_path?: string;
  repository_id?: string;
}

export interface Phase0ComponentItem {
  id: string;
  kind: string;
  name: string;
  qualified_name: string;
  repository_id?: string;
}

export interface Phase0APIEndpointItem {
  id: string;
  method: string;
  path: string;
  framework?: string;
  handler?: string;
  service_id?: string;
}

export interface Phase0DatastoreItem {
  id: string;
  name: string;
  datastore_type: string;
  host?: string;
  database_name?: string;
}

export interface Phase0DocumentItem {
  id: string;
  doc_type: string;
  title: string;
  format?: string;
}

export interface Phase0DependencyItem {
  id: string;
  dep_type: string;
  from_component_id?: string;
  to_component_id?: string;
  repository_id?: string;
  created_at?: string;
  /** Persisted by the extractor that found the edge — used to explain it. */
  evidence?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface Phase0ServiceRelationshipItem {
  id: string;
  relationship_type: string;
  from_service: string;
  to_service: string;
}

export interface Phase0ServiceDatastoreLinkItem {
  id: string;
  link_type: string;
  service: string;
  datastore: string;
}

export interface UnderstandingWorkerStatus {
  id: string;
  name: string;
  layer: string;
  task_type: string;
  status: string;
  task_id?: string;
  error?: string;
  description?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  insight_count?: number;
  last_insight_at?: string;
}

export interface UnderstandingStatusSummary {
  total: number;
  completed: number;
  running: number;
  pending: number;
  failed: number;
  insight_count: number;
  started_at?: string;
  completed_at?: string;
  generated_at?: string;
}

export interface UnderstandingStatus {
  parent_status: string;
  workers: UnderstandingWorkerStatus[];
  /** Absent on older API builds. */
  summary?: UnderstandingStatusSummary;
}

export interface WorkerInsightRecord {
  id: string;
  worker_id: string;
  insight_layer: string;
  title: string;
  summary: string;
  diagram_kind?: string;
  diagram_mermaid?: string;
  payload?: Record<string, unknown>;
  evidence_refs?: SkillEvidenceRef[];
  created_at: string;
}

export function useUnderstandingStatus(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  const query = useQuery({
    queryKey: understandingStatusKey(projectId),
    queryFn: ({ signal }) =>
      apiClientWithMeta<UnderstandingStatus>(`/projects/${projectId}/understanding/status`, { signal }),
    enabled: !!projectId,
    refetchInterval: (q) =>
      poll
        ? refetchWhenActive(q, {
            intervalMs: 3_000,
            shouldPoll: (data) => understandingStillRunning(data?.data),
          })
        : false,
    staleTime: poll ? 3_000 : 5 * 60_000,
    retry: (_, error) =>
      !(error instanceof Error && /404|not found/i.test(error.message)),
  });
  return {
    ...query,
    data: query.data?.data,
    dataGeneration: query.data?.dataGeneration,
  };
}

export function useUnderstandingInsights(projectId: string, enabled = true, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  const query = useQuery({
    queryKey: understandingInsightsKey(projectId),
    queryFn: async () => {
      const { data, dataGeneration } = await apiClientWithMeta<WorkerInsightRecord[]>(
        `/projects/${projectId}/understanding/insights`,
      );
      return { items: data ?? [], dataGeneration };
    },
    enabled: !!projectId && enabled,
    refetchInterval: (q) => (poll ? refetchWhenActive(q, { intervalMs: 5_000 }) : false),
    staleTime: poll ? 5_000 : 5 * 60_000,
    retry: (_, error) =>
      !(error instanceof Error && /404|not found/i.test(error.message)),
  });
  return {
    ...query,
    data: query.data?.items,
    dataGeneration: query.data?.dataGeneration,
  };
}

export function useRerunUnderstanding(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ message: string; mode: string }>(`/projects/${projectId}/understanding/rerun`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: understandingStatusKey(projectId) });
      queryClient.invalidateQueries({ queryKey: understandingInsightsKey(projectId) });
    },
  });
}

export function useEnrichDiagrams(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ message: string }>(`/projects/${projectId}/understanding/enrich-diagrams`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: understandingInsightsKey(projectId) });
    },
  });
}

export interface NarrativeNodeDossier {
  role: string;
  summary: string;
  highlights?: string[];
  file_path?: string;
  language?: string;
  lines_of_code?: number;
  complexity_score?: number;
  connections?: string[];
  content: string;
  encoding: 'plain' | 'base64' | 'none';
  content_note?: string;
  truncated?: boolean;
}

export interface NarrativeNodeDossierInput {
  node_id: string;
  label: string;
  caption: string;
  kind: string;
  stage: string;
  file_path?: string;
  worker_id?: string;
  technical_name?: string;
  swimlane?: string;
  component_id?: string;
  upstream?: string[];
  downstream?: string[];
}

export function useNarrativeNodeDossier(projectId: string, input: NarrativeNodeDossierInput | null) {
  return useQuery({
    queryKey: [
      'projects',
      projectId,
      'narrative-dossier',
      input?.node_id,
      input?.label,
      input?.file_path,
      input?.component_id,
      input?.technical_name,
    ],
    queryFn: () =>
      apiClient<NarrativeNodeDossier>(`/projects/${projectId}/phase0/narrative-node/dossier`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    enabled: !!projectId && !!input?.node_id,
    staleTime: 120_000,
    retry: 1,
  });
}

/** True when insights were generated by the legacy mindmap template (pre–Phase 0 wiring). */
export function insightsLookStale(insights: WorkerInsightRecord[]): boolean {
  return insights.some(
    (ins) =>
      ins.diagram_mermaid?.includes('mindmap') &&
      ins.diagram_mermaid?.includes('root((Patterns))')
  );
}

export function usePhase0InventoryList<T>(
  projectId: string,
  kind: Phase0InventoryKind,
  enabled: boolean
) {
  const path =
    kind === 'artifacts'
      ? `/projects/${projectId}/phase0/artifacts`
      : `/projects/${projectId}/phase0/${kind}`;
  return useQuery({
    queryKey: ['projects', projectId, 'phase0', kind],
    queryFn: async () => {
      try {
        return await apiClient<T[]>(path);
      } catch (e) {
        if (e instanceof Error && /404|not found/i.test(e.message)) {
          return [] as T[];
        }
        throw e;
      }
    },
    enabled: !!projectId && enabled,
  });
}

export interface MuleSupplementResult {
  added: number;
  flow_count: number;
  edge_count: number;
  components: number;
}

export interface BehavioralSupplementResult {
  added: number;
  behavioral_edges: number;
}

/** Backfill call-graph and file-reference behavioral edges from stored source. */
export async function supplementBehavioralInventory(
  projectId: string,
): Promise<BehavioralSupplementResult> {
  return apiClient<BehavioralSupplementResult>(`/projects/${projectId}/phase0/supplement-behavioral`, {
    method: 'POST',
  });
}

/** Backfill Mule flows/subflows and flow-ref edges from stored XML into phase-0 inventory. */
export async function supplementMuleInventory(projectId: string): Promise<MuleSupplementResult> {
  return apiClient<MuleSupplementResult>(`/projects/${projectId}/phase0/supplement-mule`, {
    method: 'POST',
  });
}

export interface ScannerDependencyNode {
  id: string;
  name: string;
  version?: string;
  scope: string;
  from_manifest?: string;
  direct?: boolean;
}

export interface ScannerScanResult {
  id: string;
  project_id: string;
  repository_id: string;
  repository_inventory: {
    files: { path: string; category: string; language?: string }[];
    services: { id: string; name: string; type: string; confidence: number }[];
    components: { kind: string; name: string; qualified_name: string }[];
    infra: { asset_type: string; name: string }[];
  };
  technology_inventory: {
    entries: { kind: string; name: string; byte_ratio?: number; confidence: number }[];
    language_ratios: Record<string, number>;
  };
  dependency_inventory: {
    nodes: ScannerDependencyNode[];
    edges: { from_id: string; to_id: string; edge_type: string }[];
  };
  entities: {
    id: string;
    entity_type: string;
    name: string;
    confidence: number;
    provenance?: { file_path?: string; rule?: string; confidence?: number };
    properties?: Record<string, unknown>;
  }[];
  relations: {
    id: string;
    relation_type: string;
    from_entity_id: string;
    to_entity_id: string;
    provenance?: { file_path?: string; rule?: string };
  }[];
}

export function useScannerInventory(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  const query = useQuery({
    queryKey: ['projects', projectId, 'scanner-inventory'],
    queryFn: async () => {
      try {
        const { data, dataGeneration } = await apiClientWithMeta<ScannerScanResult[] | null>(
          `/projects/${projectId}/scanner/inventory`,
        );
        return { items: data ?? [], dataGeneration };
      } catch (e) {
        if (e instanceof Error && e.message.includes('404')) {
          return { items: [] as ScannerScanResult[], dataGeneration: undefined };
        }
        throw e;
      }
    },
    enabled: !!projectId,
    staleTime: poll ? 5_000 : 120_000,
    refetchInterval: poll ? () => pollIntervalMs(5_000) : false,
    retry: (_, error) => !(error instanceof Error && error.message.includes('404')),
  });
  return {
    ...query,
    data: query.data?.items,
    dataGeneration: query.data?.dataGeneration,
  };
}

export function usePreparedRepositories(projectId: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  return useQuery({
    queryKey: ['projects', projectId, 'intake-prepared'],
    queryFn: async () => {
      try {
        const rows = await apiClient<PreparedRepository[] | null>(`/projects/${projectId}/intake/prepared`);
        return (rows ?? []).map((p) => ({
          ...p,
          risks: p.risks ?? [],
          delta: {
            has_prior_scan: p.delta?.has_prior_scan ?? false,
            added: p.delta?.added ?? [],
            modified: p.delta?.modified ?? [],
            deleted: p.delta?.deleted ?? [],
          },
        }));
      } catch (e) {
        if (e instanceof Error && e.message.includes('404')) return [];
        throw e;
      }
    },
    enabled: !!projectId,
    staleTime: poll ? 5_000 : 120_000,
    refetchInterval: poll ? () => pollIntervalMs(5_000) : false,
    retry: (_, error) => !(error instanceof Error && error.message.includes('404')),
  });
}

// Archives go up in small sequential chunks so no single request runs long.
// A one-shot multipart POST was severed after ~10s by the dev port forward,
// surfacing as "Failed to fetch" or a bare proxy 500.
const ARCHIVE_CHUNK_BYTES = 2 * 1024 * 1024;
const ARCHIVE_CHUNK_ATTEMPTS = 4;

type IntakeUploadResult = IngestionSummary & { status?: string; message?: string; filename?: string };

export async function uploadProjectArchive(
  projectId: string,
  file: File,
  options?: {
    onProgress?: (progress: { loaded: number; total: number; fileName: string }) => void;
    /** One archive of a multi-repository project: ingested as its own repository. */
    repositoryKey?: string;
  },
) {
  const uploadId = randomId();
  const path = `/projects/${projectId}/intake/upload-chunk`;
  let result: IntakeUploadResult | undefined;
  for (let offset = 0; offset < file.size || (file.size === 0 && offset === 0); offset += ARCHIVE_CHUNK_BYTES) {
    const params = new URLSearchParams({
      upload_id: uploadId,
      filename: file.name,
      label: file.name,
      offset: String(offset),
      total: String(file.size),
    });
    if (options?.repositoryKey) params.set('repository_key', options.repositoryKey);
    const chunk = file.slice(offset, offset + ARCHIVE_CHUNK_BYTES);
    result = await uploadArchiveChunk(path, params, chunk);
    options?.onProgress?.({
      loaded: bytesUploadedAfterChunk(offset, chunk.size, file.size),
      total: file.size,
      fileName: file.name,
    });
    if (file.size === 0) break;
  }
  return result as IntakeUploadResult;
}

async function uploadArchiveChunk(path: string, params: URLSearchParams, chunk: Blob) {
  for (let attempt = 1; ; attempt++) {
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}?${params}`, {
        method: 'POST',
        body: chunk,
        // authHeaders(), not apiKeyHeader(): a signed-in operator authenticates
        // with their session JWT.
        headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' },
      });
    } catch (e) {
      // Network drop: the server ignores a chunk it already stored, so retrying is safe.
      if (attempt < ARCHIVE_CHUNK_ATTEMPTS) continue;
      throw e;
    }
    handleUnauthorizedResponse(response.status, path);
    let wrapped: WrappedResponse<IntakeUploadResult>;
    try {
      wrapped = await response.json();
    } catch {
      if (response.status >= 500 && attempt < ARCHIVE_CHUNK_ATTEMPTS) continue;
      throw new ApiError(
        response.ok ? 'Upload failed: invalid server response' : `Upload failed (${response.status})`,
        response.status,
      );
    }
    if (!response.ok || !wrapped.success) {
      throw new ApiError(
        wrapped.error?.details || wrapped.error?.message || `Upload failed: ${response.status}`,
        response.status,
        wrapped.error?.code,
      );
    }
    return wrapped.data;
  }
}

export function useUploadRepositoryArchive() {
  const queryClient = useQueryClient();
  return useMutation({
    // repositoryKey is chosen by the caller before the mutation starts, so a
    // retried mutation re-sends the same key instead of adding a repository.
    // Omitted only to replace a project's single unkeyed repository.
    mutationFn: ({ projectId, file, repositoryKey }: { projectId: string; file: File; repositoryKey?: string }) =>
      uploadProjectArchive(projectId, file, { repositoryKey }),
    onMutate: async ({ projectId }) => {
      lockBootstrapGate(projectId);
      await queryClient.cancelQueries({ queryKey: ['projects', projectId] });
      queryClient.removeQueries({ queryKey: understandingStatusKey(projectId) });
      const previous = queryClient.getQueryData<Project>(['projects', projectId]);
      if (previous) {
        queryClient.setQueryData(['projects', projectId], projectPatchForUploadStarted(previous));
      }
      return { previous };
    },
    onError: (_, { projectId }, context) => {
      clearBootstrapGateLock(projectId);
      if (context?.previous) {
        queryClient.setQueryData(['projects', projectId], context.previous);
      }
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'workflow'] });
      queryClient.invalidateQueries({ queryKey: understandingStatusKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'intake-prepared'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'source-files'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ingestion-summary'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-artifacts'] });
    },
  });
}

export function useIngestProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      repositories,
    }: {
      projectId: string;
      repositories: RepoSpec[];
    }) =>
      apiClient<IngestionSummary>(`/projects/${projectId}/ingest`, {
        method: 'POST',
        body: JSON.stringify({ repositories }),
        timeoutMs: 120_000,
      }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'source-files'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ingestion-summary'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-summary'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'intake-prepared'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-graph'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-stats'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Project> }) => {
      const backendData = mapToBackendProject(data);
      const response = await apiClient<Record<string, unknown>>(`/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(backendData),
      });
      return mapBackendProject(response);
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['projects', project.id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient<void>(`/projects/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      queryClient.cancelQueries({ queryKey: ['projects', id] });
      queryClient.removeQueries({ queryKey: ['projects', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: true });
    },
  });
}

/** Transformation execution API is not available yet — callers must handle rejection. */
export function useStartTransformation() {
  return useMutation({
    mutationFn: async (_projectId: string) => {
      throw new Error('Transformation execution API is not available yet. Use Phase 2 after hand-off.');
    },
  });
}

/** Platform-wide metrics API is not available — use per-project reporting endpoints. */
export function useMetrics() {
  return {
    metrics: [] as Array<{ label: string; value: string | number }>,
    isAvailable: false,
    refetch: () => Promise.resolve(),
  };
}

/** Metrics history API is not available yet. */
export function useMetricsHistory(_days: number = 30) {
  return useQuery({
    queryKey: ['metricsHistory'],
    queryFn: async () => [] as Array<Record<string, unknown>>,
    enabled: false,
  });
}

function buildProjectNotifications(projects: Project[]): PlatformNotification[] {
  const now = new Date().toISOString();
  return projects
    .filter((p) => ['ingesting', 'analyzing', 'failed'].includes(p.status))
    .map((p) => {
      if (p.status === 'failed') {
        return {
          id: `project-failed-${p.id}`,
          type: 'error' as const,
          message: `"${p.name}" ingestion or analysis failed — open the project to retry.`,
          timestamp: p.updatedAt ?? now,
          read: false,
          projectId: p.id,
        };
      }
      if (p.status === 'ingesting') {
        return {
          id: `project-ingesting-${p.id}`,
          type: 'info' as const,
          message: `"${p.name}" is scanning your repository (Phase 0 ingestion).`,
          timestamp: p.updatedAt ?? now,
          read: false,
          projectId: p.id,
        };
      }
      return {
        id: `project-analyzing-${p.id}`,
        type: 'info' as const,
        message: `"${p.name}" is running digital workers (Phase 1 understanding).`,
        timestamp: p.updatedAt ?? now,
        read: false,
        projectId: p.id,
      };
    });
}

// Knowledge Graph (project-scoped, live API)

export interface KnowledgeGraphNode {
  id: string;
  type: string;
  label: string;
  properties?: Record<string, unknown>;
}

export interface KnowledgeGraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  weight: number;
  properties?: Record<string, unknown>;
}

export interface ProjectKnowledgeGraph {
  nodes: KnowledgeGraphNode[];
  edges: KnowledgeGraphEdge[];
}

export interface KnowledgeGraphBuildResult {
  project_id: string;
  node_count: number;
  edge_count: number;
  duration_ms: number;
  snapshot_version?: string;
  snapshot_id?: string;
  inferred_edges?: number;
  risks_found?: number;
  inventory?: ApplicationInventory;
  risks?: GraphRisk[];
}

export interface GraphRisk {
  id?: string;
  project_id?: string;
  snapshot_id?: string;
  scan_run_id?: string;
  risk_type: string;
  severity: string;
  title: string;
  description?: string;
  blast_radius?: number;
  location?: string;
  file_path?: string;
  recommended_action?: string;
  affected_node_ids?: string[];
  source?: string;
  status?: string;
  detected_at?: string;
  rule?: string;
  confidence?: number;
  line?: number;
  priority_score?: number;
  evidence_paths?: string[];
  node_labels?: string[];
  archetype?: string;
  missing_signal?: string;
  evidence?: RiskEvidence;
}

export interface RiskEvidence {
  file_path?: string;
  line?: number;
  rule?: string;
  confidence?: number;
  node_label?: string;
  node_labels?: string[];
  signals?: string[];
  file_paths?: string[];
  edge_types?: string[];
  archetype?: string;
  missing_signal?: string;
  service_type?: string;
  root_path?: string;
  gap_id?: string;
}

export interface GraphSnapshot {
  id: string;
  project_id: string;
  version: string;
  label: string;
  node_count: number;
  edge_count: number;
  created_at: string;
}

export interface ApplicationInventory {
  project_id: string;
  repository_inventory: { count: number; items: InventoryItemSummary[] };
  technology_inventory: {
    languages: Record<string, number>;
    frameworks: Record<string, number>;
    datastores: Record<string, number>;
    service_types: Record<string, number>;
  };
  dependency_inventory: { by_edge_type: Record<string, number>; total_edges: number };
  application_rollup: ApplicationServiceView[];
}

export interface InventoryItemSummary {
  id: string;
  type: string;
  label: string;
}

export interface ApplicationServiceView {
  service_id: string;
  service_name: string;
  components: InventoryItemSummary[];
  apis: InventoryItemSummary[];
  datastores: InventoryItemSummary[];
  documents: InventoryItemSummary[];
  dependencies: InventoryItemSummary[];
}

export interface KnowledgeGraphStats {
  project_id: string;
  node_count: number;
  edge_count: number;
  nodes_by_type: Record<string, number>;
}

export function useProjectKnowledgeGraph(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'knowledge-graph'],
    queryFn: ({ signal }) => apiClient<ProjectKnowledgeGraph>(`/projects/${projectId}/knowledge/graph`, { signal }),
    enabled: !!projectId,
    refetchInterval: 5000,
  });
}

export function useProjectKnowledgeGraphStats(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'knowledge-stats'],
    queryFn: ({ signal }) => apiClient<KnowledgeGraphStats>(`/projects/${projectId}/knowledge/stats`, { signal }),
    enabled: !!projectId,
  });
}

export function useBuildKnowledgeGraph() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      apiClient<KnowledgeGraphBuildResult>(`/projects/${projectId}/knowledge/build`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-graph'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-stats'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-inventory'] });
      queryClient.invalidateQueries({ queryKey: projectRiskRegisterQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: knowledgeSnapshotsKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'recommendations'] });
    },
  });
}

export function useProjectKnowledgeInventory(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'knowledge-inventory'],
    queryFn: ({ signal }) => apiClient<ApplicationInventory>(`/projects/${projectId}/knowledge/inventory`, { signal }),
    enabled: !!projectId,
  });
}

export function projectRiskRegisterQueryKey(projectId: string) {
  return ['projects', projectId, 'risk-register'] as const;
}

async function fetchProjectRiskRegister(projectId: string): Promise<Phase0RiskEntry[]> {
  const data = await apiClient<Phase0RiskEntry[] | null>(`/projects/${projectId}/reporting/risks`);
  return data ?? [];
}

export function useProjectRiskRegister(projectId: string) {
  return useQuery({
    queryKey: projectRiskRegisterQueryKey(projectId),
    queryFn: () => fetchProjectRiskRegister(projectId),
    enabled: !!projectId,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('404')) return false;
      return failureCount < 1;
    },
  });
}

export function useProjectKnowledgeRisks(projectId: string) {
  return useProjectRiskRegister(projectId);
}

export function useProjectKnowledgeSnapshots(projectId: string) {
  return useQuery({
    queryKey: knowledgeSnapshotsKey(projectId),
    queryFn: async () => {
      const data = await apiClient<GraphSnapshot[] | null>(
        `/projects/${projectId}/knowledge/snapshots`
      );
      return data ?? [];
    },
    enabled: !!projectId,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('404')) return false;
      return failureCount < 1;
    },
  });
}

/** POST /projects/:id/knowledge/snapshots/:version/restore — roll the graph back to a snapshot. */
export function useRestoreKnowledgeSnapshot(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: string) =>
      apiClient<GraphSnapshot | { restored?: boolean; version?: string } | null>(
        `/projects/${projectId}/knowledge/snapshots/${encodeURIComponent(version)}/restore`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-graph'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-stats'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-inventory'] });
      queryClient.invalidateQueries({ queryKey: knowledgeSnapshotsKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'baseline'] });
    },
  });
}

// --- Artifact tree (Task 0.4) ---

export interface ArtifactTreeNode {
  id: string;
  label: string;
  node_type: string;
  children?: ArtifactTreeNode[];
  color?: string;
  icon?: string;
  provenance?: {
    file_path?: string;
    start_line?: number;
    end_line?: number;
    parser?: string;
    confidence?: number;
    repository?: string;
    qualified_name?: string;
  };
  change_status?: string;
  change_reason?: string;
  risk_markers?: Array<{
    risk_type: string;
    severity: string;
    title: string;
    blast_radius?: number;
  }>;
  properties?: Record<string, unknown>;
  related_edges?: Array<{
    edge_type: string;
    target_id: string;
    target_label?: string;
    direction: string;
  }>;
  source_snippet?: string;
  confidence?: number;
  last_seen?: string;
  entity_id?: string;
}

export interface ArtifactTree {
  project_id: string;
  generated_at: string;
  root: ArtifactTreeNode;
  node_count: number;
  change_summary: {
    added: number;
    modified: number;
    deleted: number;
    unchanged: number;
  };
}

export function useProjectArtifactTree(
  projectId: string,
  opts?: { snippet?: boolean }
) {
  const snippet = opts?.snippet ? 'true' : 'false';
  return useQuery({
    queryKey: ['projects', projectId, 'artifact-tree', snippet],
    queryFn: ({ signal }) =>
      apiClient<ArtifactTree>(`/projects/${projectId}/artifacts/tree?snippet=${snippet}`, { signal }),
    enabled: !!projectId,
  });
}

export type ArtifactTreeExportFormat = 'json' | 'csv' | 'graphml';

export function useExportArtifactTree(projectId: string) {
  return useMutation({
    mutationFn: (format: ArtifactTreeExportFormat) =>
      downloadApiBlob(
        `/projects/${projectId}/artifacts/export?format=${format}&snippet=false`,
        `artifact-tree-${projectId}.${format}`,
      ),
  });
}

// --- Phase 0 reporting (Task 0.5) ---

export interface Phase0Report {
  project_id: string;
  project_name?: string;
  generated_at: string;
  coverage: {
    files_seen: number;
    files_parsed: number;
    files_skipped: number;
    skipped_by_reason: Record<string, number>;
    languages: Record<string, number>;
    frameworks: string[];
    parse_success_rate: number;
  };
  assets: {
    services_by_type: Record<string, number>;
    components_by_kind: Record<string, number>;
    apis: number;
    databases: number;
    configs: number;
    docs: number;
    dependencies: { total: number; direct: number; transitive: number };
  };
  quality: {
    code_quality: number;
    security: number;
    test_coverage: number;
    complexity: number;
    documentation_coverage: number;
    overall_score: number;
    avg_complexity_score: number;
    avg_technical_debt_minutes: number;
  };
  graph: {
    node_count: number;
    edge_count: number;
    nodes_by_type: Record<string, number>;
    community_count: number;
    average_degree: number;
    orphan_count: number;
    circular_dependency_count: number;
  };
  performance: {
    stage_latencies_ms: Record<string, string>;
    total_wall_time: string;
    avg_confidence: number;
    retry_count: number;
  };
  changes: {
    files_added: number;
    files_modified: number;
    files_deleted: number;
    new_dependencies: number;
    new_risks: number;
    risks_resolved: number;
    prior_scan_at?: string;
  };
  risks: Phase0RiskEntry[];
  links: {
    artifact_tree: string;
    knowledge_graph: string;
    ingestion: string;
  };
}

export interface Phase0RiskEntry {
  id: string;
  project_id?: string;
  snapshot_id?: string;
  scan_run_id?: string;
  risk_type: string;
  severity: string;
  title: string;
  description?: string;
  blast_radius?: number;
  location?: string;
  file_path?: string;
  recommended_action?: string;
  affected_node_ids?: string[];
  source?: string;
  status?: string;
  detected_at?: string;
  rule?: string;
  confidence?: number;
  line?: number;
  priority_score?: number;
  evidence_paths?: string[];
  node_labels?: string[];
  archetype?: string;
  missing_signal?: string;
  evidence?: RiskEvidence;
}

export interface RecommendationItem {
  risk_id: string;
  risk_type: string;
  severity: string;
  title: string;
  action: string;
  deep_links?: Record<string, string>;
}

export interface RecommendationBundle {
  id: string;
  title: string;
  category: string;
  severity: string;
  items: RecommendationItem[];
}

export interface RecommendationPlan {
  project_id: string;
  generated_at: string;
  bundles: RecommendationBundle[];
  workflow_steps: string[];
}

export interface GapAnalysisReport {
  project_id: string;
  project_name?: string;
  generated_at: string;
  overall_correctness?: number;
  executive_summary?: {
    overall_score_pct?: number;
    graph_health?: string;
    critical_gap_count?: number;
    high_gap_count?: number;
    inferred_edge_pct?: number;
    edge_to_node_ratio?: number;
    sparse_graph?: boolean;
    key_findings?: string[];
  };
  gaps?: Array<{
    id?: string;
    gap_id?: string;
    category: string;
    title: string;
    severity: string;
    recommended_fix?: string;
    evidence?: string[];
  }>;
  top_10_gaps?: string[];
  top_10_risks?: string[];
  top_10_improvements?: string[];
}

export interface Phase0AuditEntry {
  id: string;
  action: string;
  parser: string;
  rule_fired: string;
  operator: string;
  file_path: string;
  entity_type: string;
  entity_id: string;
  details: Record<string, unknown>;
  created_at: string;
}

export function useProjectPhase0Metrics(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'phase0-metrics'],
    queryFn: ({ signal }) => apiClient<Phase0Report>(`/projects/${projectId}/reporting/metrics`, { signal }),
    enabled: !!projectId,
  });
}

export function useProjectRecommendations(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'recommendations'],
    queryFn: async () => {
      const plan = await apiClient<RecommendationPlan | null>(
        `/projects/${projectId}/recommendations`,
      );
      if (!plan) return null;
      return {
        ...plan,
        bundles: (plan.bundles ?? []).map((b) => ({
          ...b,
          items: b.items ?? [],
        })),
        workflow_steps: plan.workflow_steps ?? [],
      };
    },
    enabled: !!projectId,
  });
}

export function useProjectGapAnalysis(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'gap-analysis'],
    queryFn: ({ signal }) => apiClient<GapAnalysisReport>(`/projects/${projectId}/gap-analysis`, { signal }),
    enabled: !!projectId,
  });
}

export function useProjectPhase0Audit(projectId: string, limit = 200) {
  return useQuery({
    queryKey: ['projects', projectId, 'phase0-audit', limit],
    queryFn: ({ signal }) => apiClient<Phase0AuditEntry[]>(`/projects/${projectId}/reporting/audit?limit=${limit}`, { signal }),
    enabled: !!projectId,
  });
}

export function useExportPhase0Report(projectId: string) {
  return useMutation({
    mutationFn: (format: 'markdown' | 'json') =>
      downloadApiBlob(
        `/projects/${projectId}/reporting/report?format=${format}`,
        `phase0-report-${projectId}.${format === 'json' ? 'json' : 'md'}`,
      ),
  });
}

// --- Phase 0 handoff (Task 0.6) ---

export interface Phase0HandoffRecord {
  id?: string;
  project_id: string;
  snapshot_id?: string;
  snapshot_version?: string;
  rollback_snapshot_version?: string;
  status: string;
  accepted_at?: string;
  accepted_by?: string;
  pheromone_emitted?: boolean;
  worker_dispatch?: Array<{
    worker_type: string;
    task_id: string;
    swarm_task_id?: string;
    description: string;
  }>;
  summary: {
    project_name?: string;
    snapshot_version: string;
    snapshot_label: string;
    node_count: number;
    edge_count: number;
    service_count: number;
    component_count: number;
    api_count: number;
    database_count: number;
    document_count: number;
    top_risks: Array<{
      id?: string;
      severity: string;
      title: string;
      location: string;
      risk_type?: string;
      file_path?: string;
      source?: string;
      recommended_action?: string;
    }>;
    report_link: string;
    artifact_tree_link: string;
    knowledge_graph_link: string;
    ready_for_phase1: boolean;
  };
  created_at?: string;
  updated_at?: string;
}

export function usePhase0HandoffStatus(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'phase0-handoff'],
    queryFn: ({ signal }) => apiClient<Phase0HandoffRecord>(`/projects/${projectId}/phase0/handoff`, { signal }),
    enabled: !!projectId,
  });
}

export function useDeliverPhase0Handoff(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ handoff: Phase0HandoffRecord }>(`/projects/${projectId}/phase0/deliver`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-handoff'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useAcceptPhase0Handoff(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // accepted_by is ignored by the server for signed-in operators — the
    // sign-off is attributed to the authenticated identity so the audit
    // record cannot be self-named. notes is carried into that audit entry.
    mutationFn: (body: { accepted_by?: string; notes?: string } = {}) =>
      apiClient<Phase0HandoffRecord>(`/projects/${projectId}/phase0/accept`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-handoff'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRollbackPhase0Handoff(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ message: string }>(`/projects/${projectId}/phase0/rollback`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phase0-handoff'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'knowledge-graph'] });
    },
  });
}

export function useSearchProjectKnowledge(projectId: string) {
  return useMutation({
    mutationFn: ({ query, limit = 10 }: { query: string; limit?: number }) =>
      apiClient<{ results: KnowledgeGraphNode[]; total: number }>(
        `/projects/${projectId}/knowledge/search`,
        {
          method: 'POST',
          body: JSON.stringify({ query, limit }),
        }
      ),
  });
}

// --- Platform skills (integrated status) ---

export type SkillStatus =
  | 'pending'
  | 'running'
  | 'ready'
  | 'partial'
  | 'failed'
  | 'unavailable'
  | 'locked_preview';

export interface SkillEvidenceRef {
  kind: string;
  id?: string;
  label?: string;
  detail?: string;
}

export interface ProjectSkillState {
  id: string;
  name: string;
  tier: number;
  summary: string;
  status: SkillStatus;
  primary_phase: string;
  ui_attachment: 'tab' | 'change_request' | 'ams_dashboard';
  provider: string;
  linked_workers: string[];
  linked_tasks: { type: string; status: string; id?: string }[];
  evidence: SkillEvidenceRef[];
  evidence_count: number;
  summary_text: string;
  locked_message?: string;
  moksha_skill?: string;
  status_in_context?: SkillStatus;
  check_result?: 'pass' | 'fail' | 'warn' | 'pending';
}

export interface ChangeRequest {
  id: string;
  project_id: string;
  external_ref?: string;
  title: string;
  description: string;
  status: string;
  work_type?: 'customization' | 'enhancement';
  scope: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ChangeRequestSkillsResponse {
  project_id: string;
  change_request_id: string;
  computed_at: string;
  skills: ProjectSkillState[];
}

export interface ProjectSkillsResponse {
  project_id: string;
  computed_at: string;
  skills: ProjectSkillState[];
}

export function useProjectSkills(projectId: string, uiAttachment?: string, options?: { poll?: boolean }) {
  const poll = options?.poll ?? true;
  const qs = uiAttachment ? `?ui_attachment=${encodeURIComponent(uiAttachment)}` : '';
  const query = useQuery({
    queryKey: ['projects', projectId, 'skills', uiAttachment ?? 'all'],
    queryFn: ({ signal }) => apiClientWithMeta<ProjectSkillsResponse>(`/projects/${projectId}/skills${qs}`, { signal }),
    enabled: !!projectId,
    refetchInterval: poll ? 5000 : false,
    staleTime: poll ? 0 : 5 * 60 * 1000,
  });
  return {
    ...query,
    data: query.data?.data,
    dataGeneration: query.data?.dataGeneration,
  };
}

export function useProjectSkill(projectId: string, skillId: string, enabled = true) {
  return useQuery({
    queryKey: ['projects', projectId, 'skills', skillId],
    queryFn: ({ signal }) => apiClient<ProjectSkillState>(`/projects/${projectId}/skills/${skillId}`, { signal }),
    enabled: !!projectId && !!skillId && enabled,
  });
}

/**
 * One fetcher for ['projects', id, 'change-requests'] so every reader shares
 * the cache entry. The cached value is the `{ data, dataGeneration }` envelope
 * from apiClientWithMeta; readers unwrap `.data`.
 */
function fetchChangeRequests(projectId: string) {
  return apiClientWithMeta<{ change_requests: ChangeRequest[] }>(`/projects/${projectId}/change-requests`);
}

export function useChangeRequests(projectId: string) {
  const query = useQuery({
    queryKey: ['projects', projectId, 'change-requests'],
    queryFn: () => fetchChangeRequests(projectId),
    enabled: !!projectId,
  });
  return {
    ...query,
    data: query.data?.data,
    dataGeneration: query.data?.dataGeneration,
  };
}

export interface PortfolioCrRow {
  projectId: string;
  projectName: string;
  openCount: number;
  latestCr?: ChangeRequest;
}

export function usePortfolioChangeRequests(
  projectIds: string[],
  projects: Pick<Project, 'id' | 'name'>[]
) {
  const queries = useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ['projects', id, 'change-requests'],
      queryFn: () => fetchChangeRequests(id),
      enabled: !!id,
      staleTime: 30000,
    })),
  });

  const rows = useMemo((): PortfolioCrRow[] => {
    const nameById = new Map(projects.map((p) => [p.id, p.name]));
    return projectIds
      .map((id, i) => {
        const crs = queries[i]?.data?.data?.change_requests ?? [];
        const openCount = crs.filter((cr) => cr.status === 'draft' || cr.status === 'review').length;
        const latestCr = [...crs].sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )[0];
        return {
          projectId: id,
          projectName: nameById.get(id) ?? id.slice(0, 8),
          openCount,
          latestCr,
        };
      })
      .filter((r) => r.openCount > 0 || r.latestCr)
      .sort((a, b) => b.openCount - a.openCount);
  }, [projectIds, projects, queries]);

  const openTotal = rows.reduce((sum, r) => sum + r.openCount, 0);
  const isLoading = queries.some((q) => q.isLoading);

  const sparkline7d = useMemo(() => {
    const dates: string[] = [];
    for (const q of queries) {
      for (const cr of q.data?.data?.change_requests ?? []) {
        if (cr.created_at) dates.push(cr.created_at);
      }
    }
    return bucketLastNDays(dates, 7);
  }, [queries]);

  return { rows, openTotal, isLoading, sparkline7d };
}

export function useChangeRequestSkills(projectId: string, crId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'change-requests', crId, 'skills'],
    queryFn: () =>
      apiClient<ChangeRequestSkillsResponse>(
        `/projects/${projectId}/change-requests/${crId}/skills`
      ),
    enabled: !!projectId && !!crId,
    refetchInterval: 5000,
  });
}

export interface SkillRunResult {
  skill_id: string;
  moksha_skill?: string;
  provider?: string;
  status: string;
  message: string;
  execution_id?: string;
  moksha_request?: string;
  change_request_id?: string;
}

export interface RemediationRunResult {
  task_id: string;
  change_request_id: string;
  status: string;
  worker: string;
  message: string;
}

export function useRunRemediation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { change_request_id: string; plan_summary?: string }) =>
      apiClient<RemediationRunResult>(`/projects/${projectId}/remediation/run`, {
        method: 'POST',
        body: JSON.stringify(body),
        timeoutMs: 120_000,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillExecutionsKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tasks'] });
    },
  });
}

export function useCreateChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      external_ref?: string;
      title: string;
      description?: string;
      work_type?: 'customization' | 'enhancement';
      scope?: Record<string, unknown>;
    }) =>
      apiClient<ChangeRequest>(`/projects/${projectId}/change-requests`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'change-requests'] });
    },
  });
}

export interface SkillExecutionRecord {
  id: string;
  project_id: string;
  change_request_id?: string;
  skill_id: string;
  status: string;
  phase: string;
  provider: string;
  summary?: string;
  computed_at: string;
  evidence_refs?: unknown[];
}

export function useSkillExecutions(projectId: string, changeRequestId?: string) {
  return useQuery({
    queryKey: skillExecutionsKey(projectId, changeRequestId ?? 'all'),
    queryFn: async () => {
      const qs = changeRequestId
        ? `?change_request_id=${encodeURIComponent(changeRequestId)}`
        : '';
      const response = await apiClient<{ executions: SkillExecutionRecord[] }>(
        `/projects/${projectId}/skill-executions${qs}`
      );
      return response.executions ?? [];
    },
    enabled: !!projectId,
    refetchInterval: 10000,
  });
}

export function useUpdateChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      crId,
      ...body
    }: {
      crId: string;
      status?: string;
      title?: string;
      description?: string;
      scope?: Record<string, unknown>;
    }) =>
      apiClient<ChangeRequest>(`/projects/${projectId}/change-requests/${crId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'change-requests'] });
    },
  });
}

export function useRunProjectSkill(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      skillId,
      changeRequestId,
    }: {
      skillId: string;
      changeRequestId?: string;
    }) =>
      apiClient<SkillRunResult>(`/projects/${projectId}/skills/${skillId}/run`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'execute',
          change_request_id: changeRequestId,
        }),
        timeoutMs: 120_000,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'skills'] });
      queryClient.invalidateQueries({ queryKey: skillExecutionsKey(projectId) });
    },
  });
}

export function useRunChangeRequestSkill(projectId: string, crId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) =>
      apiClient<SkillRunResult>(
        `/projects/${projectId}/change-requests/${crId}/skills/${skillId}/run`,
        { method: 'POST', body: JSON.stringify({ action: 'execute' }), timeoutMs: 120_000 }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'change-requests', crId, 'skills'],
      });
      queryClient.invalidateQueries({ queryKey: skillExecutionsKey(projectId) });
    },
  });
}

// AMS incidents (operational alerts)

export interface AmsMitigationStep {
  step: string;
  priority?: number;
}

export interface AmsVerificationCheck {
  id: string;
  label: string;
  status: 'pass' | 'fail' | 'warn' | 'pending' | string;
  source: string;
  detail?: string;
}

export interface AmsImpactAssessment {
  summary?: string;
  risk_level?: string;
  affected_components?: string[];
  affected_integrations?: string[];
  suggested_work_type?: 'customization' | 'enhancement' | string;
}

import type {
  ApplyMissionDecisionInput,
  MissionDecision,
  OperationalMissionView,
  OperationalRecord,
  RuntimeVerificationDetail,
} from '../types/operations';

export interface AmsIncident {
  id: string;
  project_id: string;
  project_name?: string;
  source: string;
  severity: string;
  status: string;
  title: string;
  summary?: string;
  description?: string;
  raw_payload?: Record<string, unknown>;
  transaction_id?: string;
  application_key?: string;
  rca: Array<Record<string, unknown>>;
  mitigation: AmsMitigationStep[];
  moksha_execution_id?: string;
  kg_node_id?: string;
  change_request_id?: string;
  verification_status?: string;
  verification_checks?: AmsVerificationCheck[];
  impact_assessment?: AmsImpactAssessment;
  suggested_work_type?: string;
  promoted_stage?: string;
  linked_session_id?: string;
  mission_decision_id?: string;
  mission_stage?: string;
  engineering_validation_status?: string;
  runtime_verification_status?: string;
  runtime_verification?: RuntimeVerificationDetail;
  episode_id?: string;
  resolution?: string;
  operational_record?: OperationalRecord;
  closed_at?: string | null;
  mission?: OperationalMissionView;
  read_at?: string | null;
  /** Per-project number from the database (every source); display_id is INC000123. */
  incident_number?: number;
  display_id?: string;
  /** Service-level assessment and clock (P1..P4, ITIL impact/urgency vocabulary). */
  priority?: string;
  impact?: string;
  urgency?: string;
  assignee_email?: string;
  assigned_at?: string | null;
  acknowledged_at?: string | null;
  resolved_at?: string | null;
  response_due_at?: string | null;
  resolution_due_at?: string | null;
  response_breached?: boolean;
  resolution_breached?: boolean;
  /** Reported (source = manual) incidents only. */
  category?: string;
  subcategory?: string;
  assignment_group?: string;
  created_by?: string;
  created_by_id?: string;
  attachment_count?: number;
  created_at: string;
  updated_at: string;
}

export const amsQueryKeys = {
  inbox: () => ['ams', 'inbox'] as const,
  incidents: (projectId: string) => ['projects', projectId, 'ams', 'incidents'] as const,
  incident: (projectId: string, incidentId: string) =>
    ['projects', projectId, 'ams', 'incidents', incidentId] as const,
  missionDecision: (projectId: string, incidentId: string) =>
    ['ams', projectId, 'incidents', incidentId, 'mission-decision'] as const,
  operationalExecution: (projectId: string, incidentId: string) =>
    ['ams', projectId, 'incidents', incidentId, 'operational-execution'] as const,
  rcaOwnership: (projectId: string, incidentId: string) =>
    ['ams', projectId, 'incidents', incidentId, 'rca-ownership'] as const,
  implementationPlan: (projectId: string, incidentId: string) =>
    ['ams', projectId, 'incidents', incidentId, 'implementation-plan'] as const,
  signals: (projectId: string) => ['ams', projectId, 'signals'] as const,
};

export interface AmsLogIngestResult {
  ingest_id: string;
  filename: string;
  application_key?: string;
  environment?: string;
  source_kind?: string;
  line_count: number;
  error_line_count: number;
  incidents_created: number;
  incident_ids: string[];
  auto_investigate_count?: number;
  /** Distinct failure signatures found; above the per-upload cap only the first became incidents. */
  signature_count?: number;
  truncated?: boolean;
}

export interface AmsLogIngestDeleteResult {
  ingest_id: string;
  incidents_deleted: number;
  investigation_runs_deleted: number;
}

function amsSeverityToNotifType(severity: string): PlatformNotification['type'] {
  if (severity === 'critical' || severity === 'high') return 'error';
  if (severity === 'medium') return 'warning';
  if (severity === 'low') return 'info';
  return 'warning';
}

function buildAmsInboxNotifications(incidents: AmsIncident[]): PlatformNotification[] {
  return incidents
    .filter((i) => !i.read_at)
    .map((i) => ({
      id: `ams-incident-${i.id}`,
      type: amsSeverityToNotifType(i.severity),
      message: `[${i.project_name ?? 'Project'}] ${i.title}`,
      timestamp: i.created_at,
      read: false,
      projectId: i.project_id,
      incidentId: i.id,
      href: `/projects/${i.project_id}/operate/incident?incident=${encodeURIComponent(i.id)}`,
    }));
}

export function useAmsInbox() {
  const query = useQuery({
    queryKey: ['ams', 'inbox'],
    queryFn: async () => {
      const { data, dataGeneration } = await apiClientWithMeta<AmsIncident[] | null>('/ams/inbox');
      return { items: data ?? [], dataGeneration };
    },
    refetchInterval: 30_000,
  });
  return {
    ...query,
    data: query.data?.items,
    dataGeneration: query.data?.dataGeneration,
  };
}

export interface ExecutionTrendDay {
  date: string;
  attempted: number;
  succeeded: number;
}

export interface ExecutionStrategyCount {
  strategy: string;
  count: number;
  source?: 'operational' | 'skill' | 'engineering' | string;
}

export interface ExecutionSourceCounts {
  attempted: number;
  succeeded: number;
}

export interface ExecutionProjectCount {
  project_id: string;
  project_name?: string;
  attempted: number;
  succeeded: number;
}

export interface ExecutionTrend {
  days: ExecutionTrendDay[];
  strategies: ExecutionStrategyCount[];
  total_attempted: number;
  total_succeeded: number;
  sources?: {
    operational: ExecutionSourceCounts;
    skills: ExecutionSourceCounts;
    engineering: ExecutionSourceCounts;
  };
  by_project?: ExecutionProjectCount[];
}

export function useExecutionTrend(days = 14) {
  return useQuery({
    queryKey: ['platform', 'overview', 'execution-trend', days],
    queryFn: ({ signal }) => apiClient<ExecutionTrend>(`/platform/overview/execution-trend?days=${days}`, { signal }),
    refetchInterval: 60_000,
  });
}

export type ExecutorReadinessState =
  | 'ready'
  | 'adapter_missing'
  | 'unavailable'
  | 'disabled'
  | 'running';

export interface ExecutorReadinessEntry {
  id: string;
  label: string;
  strategy?: string;
  state: ExecutorReadinessState;
  status_label: string;
  reason?: string;
  adapter?: string;
  core: boolean;
}

export interface ExecutorReadinessReport {
  executors: ExecutorReadinessEntry[];
  core_available: number;
  optional_available: number;
}

export function useExecutorReadiness() {
  return useQuery({
    queryKey: ['platform', 'overview', 'executor-readiness'],
    queryFn: ({ signal }) => apiClient<ExecutorReadinessReport>(`/platform/overview/executor-readiness`, { signal }),
    refetchInterval: 60_000,
  });
}

export interface AmsLogIngestRecord {
  id: string;
  project_id: string;
  filename: string;
  application_key?: string;
  environment?: string;
  source_kind?: string;
  line_count: number;
  error_count: number;
  incidents_created: number;
  created_at: string;
}

export interface AmsSignalsSummary {
  generated_at: string;
  total_incidents: number;
  log_signals: number;
  email_signals: number;
  unverified: number;
  verified: number;
  enhancement_candidates: number;
  enhancement_crs: number;
  customization_crs: number;
  open_critical: number;
  notification_apis: number;
  recent_log_ingests: number;
}

export interface AmsSignalsSummaryResponse {
  summary: AmsSignalsSummary;
  baseline_locked?: boolean;
  baseline_version?: string;
  drift_deltas?: Record<string, number>;
  test_promotions?: number;
  prod_promotions?: number;
}

export function useAmsSignalsSummary(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'signals', 'summary'],
    queryFn: ({ signal }) => apiClient<AmsSignalsSummaryResponse>(`/projects/${projectId}/ams/signals/summary`, { signal }),
    enabled: Boolean(projectId),
    refetchInterval: 15_000,
  });
}

export interface AmsEmailMailbox {
  id: string;
  project_id: string;
  label: string;
  imap_host: string;
  imap_port: number;
  use_tls: boolean;
  username: string;
  folder: string;
  enabled: boolean;
  has_password: boolean;
  last_sync_at?: string;
  last_sync_count?: number;
  last_sync_messages_analyzed?: number;
  last_sync_project_relevant?: number;
  last_error?: string;
  /** Present after a save while the shared inbox is enabled: the mail-source link. */
  shared_inbox?: { connected: boolean; source_id?: string; rules?: number; warnings?: string[]; error?: string };
}

export interface AmsMailboxSyncResult {
  scanned: number;
  ingested: number;
  skipped: number;
  filtered?: number;
  filtered_permanent?: number;
  filtered_reprocessable?: number;
  errors: number;
  messages_analyzed?: number;
  project_relevant?: number;
  rejected_sample_subjects?: string[];
  /** Shared inbox: stored messages removed because the mailbox no longer holds them. */
  messages_deleted?: number;
  /** Incidents removed with those messages. */
  incidents_deleted?: number;
  /** Incidents kept despite their mail being deleted, because engineering work is attached. */
  incidents_kept?: number;
}

export interface AmsMailboxMessagePreview {
  uid: string;
  subject: string;
  from: string;
  received_at?: string;
  processed: boolean;
  ingested_incident_id?: string;
  project_relevant: boolean;
  project_relevance_score: number;
  notification_category: string;
  stopped_reason?: string;
  recommended: boolean;
  can_reprocess: boolean;
  /** Marked processed without an incident, or rejected as hard inbox noise. */
  suppressed: boolean;
  from_name?: string;
  from_address?: string;
  /** Itemized contributions to project_relevance_score. */
  signals: AmsRelevanceSignal[];
  application_key?: string;
  environment?: string;
  transaction_id?: string;
  body?: string;
}

export interface AmsRelevanceSignal {
  label: string;
  points: number;
}

export interface AmsMailboxPreviewResult {
  messages: AmsMailboxMessagePreview[];
  limit: number;
  /** Relevance score at which a message is recommended. */
  threshold: number;
}

export interface AmsMailboxSyncOptions {
  force?: boolean;
  message_uids?: string[];
  operator_selected_uids?: string[];
}

export function formatMailboxSyncMessage(result: AmsMailboxSyncResult): string {
  const analyzed = result.messages_analyzed ?? result.scanned;
  const relevant = result.project_relevant ?? result.ingested;
  let msg = `Showing ${relevant} project-relevant event${relevant === 1 ? '' : 's'} out of ${analyzed} inbox message${analyzed === 1 ? '' : 's'} analyzed.`;
  if (result.filtered && result.filtered > 0) {
    const reprocessable = result.filtered_reprocessable ?? 0;
    const permanent = result.filtered_permanent ?? result.filtered - reprocessable;
    if (reprocessable > 0 && permanent > 0) {
      msg += ` ${permanent} message${permanent === 1 ? '' : 's'} suppressed as non-project noise; ${reprocessable} below threshold (can re-extract).`;
    } else if (reprocessable > 0) {
      msg += ` ${reprocessable} message${reprocessable === 1 ? '' : 's'} below relevance threshold — use Review inbox to extract manually.`;
    } else {
      msg += ` ${result.filtered} message${result.filtered === 1 ? '' : 's'} suppressed as non-project noise.`;
    }
  }
  if (result.skipped > 0) {
    msg += ` ${result.skipped} already processed.`;
  }
  if (relevant === 0 && result.filtered && result.filtered > 0 && result.rejected_sample_subjects?.length) {
    msg += ` Examples ignored: ${result.rejected_sample_subjects.slice(0, 2).join('; ')}.`;
  }
  const deleted = result.messages_deleted ?? 0;
  if (deleted > 0) {
    const incidents = result.incidents_deleted ?? 0;
    msg += ` Removed ${deleted} message${deleted === 1 ? '' : 's'} deleted from the mailbox`;
    msg += incidents > 0 ? ` and ${incidents} incident${incidents === 1 ? '' : 's'} raised from them.` : '.';
  }
  const kept = result.incidents_kept ?? 0;
  if (kept > 0) {
    msg += ` Kept ${kept} incident${kept === 1 ? '' : 's'} whose mail was deleted because engineering work is attached.`;
  }
  return msg;
}

export function useAmsEmailMailbox(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'email-mailbox'],
    queryFn: async () => {
      const data = await apiClient<AmsEmailMailbox | null>(`/projects/${projectId}/ams/email-mailbox`);
      return data;
    },
    enabled: Boolean(projectId),
    // The background inbox poll can set or clear last_error server-side.
    refetchInterval: (query) => refetchWhenActive(query, { intervalMs: 45_000 }),
  });
}

export function useUpsertAmsEmailMailbox(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      label?: string;
      imap_host: string;
      imap_port?: number;
      use_tls?: boolean;
      username: string;
      password?: string;
      folder?: string;
      enabled?: boolean;
    }) =>
      apiClient<AmsEmailMailbox>(`/projects/${projectId}/ams/email-mailbox`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'email-mailbox'] });
    },
  });
}

export interface AmsMailboxTestResult {
  ok: boolean;
  host: string;
  port: number;
  folder: string;
  messages: number;
}

/** Log in with unsaved settings; a blank password re-uses the stored one. */
export function useTestAmsEmailMailbox(projectId: string) {
  return useMutation({
    mutationFn: (body: {
      imap_host: string;
      imap_port?: number;
      use_tls?: boolean;
      username: string;
      password?: string;
      folder?: string;
    }) =>
      apiClient<AmsMailboxTestResult>(`/projects/${projectId}/ams/email-mailbox/test`, {
        method: 'POST',
        body: JSON.stringify(body),
        timeoutMs: 60_000,
      }),
  });
}

/** Suggest the IMAP host for an address (known providers, then MX lookup). */
export function suggestImapHost(email: string) {
  return apiClient<{ domain: string; imap_host: string; provider: string }>(
    `/ams/imap-host-suggest?email=${encodeURIComponent(email)}`,
  );
}

export function usePreviewAmsEmailMailbox(projectId: string, limit = 25) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'email-mailbox', 'preview', limit],
    queryFn: () =>
      apiClient<AmsMailboxPreviewResult>(
        `/projects/${projectId}/ams/email-mailbox/preview?limit=${limit}`,
        { timeoutMs: 180_000 },
      ),
    enabled: false,
    staleTime: 0,
  });
}

export function useSyncAmsEmailMailbox(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options?: AmsMailboxSyncOptions) =>
      apiClient<AmsMailboxSyncResult>(`/projects/${projectId}/ams/email-mailbox/sync`, {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
        timeoutMs: 300_000,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'skills'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'email-mailbox', 'preview'] });
    },
    onError: () => {
      // A failed sync rewrites last_error; show the new one.
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'email-mailbox'], exact: true });
    },
  });
}

export function useSuppressAmsEmailMessages(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageUIDs: string[]) =>
      apiClient<{ suppressed: number }>(`/projects/${projectId}/ams/email-mailbox/suppress`, {
        method: 'POST',
        body: JSON.stringify({ message_uids: messageUIDs }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'email-mailbox', 'preview'] });
    },
  });
}

// Shared inbox: stored physical messages of the project's connected mail
// sources, with the system routing decision as it concerns this project.

/** routed_here / routed_elsewhere / conflict / unattributed / unrouted. */
export type AmsEmailRoutingStatus = 'routed_here' | 'routed_elsewhere' | 'conflict' | 'unattributed' | 'unrouted';

export interface AmsEmailProjectRef {
  id: string;
  name: string;
}

export interface AmsEmailMessageRouting {
  status: AmsEmailRoutingStatus;
  decided_at?: string;
  /** The other project — present only when the caller may see it. */
  routed_to?: AmsEmailProjectRef;
  /** Conflicting projects the caller may see. */
  candidates?: AmsEmailProjectRef[];
  /** Some conflicting projects are not visible to the caller. */
  hidden_candidates?: boolean;
  imported: boolean;
  imported_by?: string;
  imported_at?: string;
  in_project: boolean;
  /** Needs routing, but this project's current rules match it. Not imported until the operator imports it. */
  rule_match?: AmsEmailRuleMatch;
  /** Needs routing, but the current rules match several projects; none is chosen. */
  rule_conflict?: AmsEmailRuleConflict;
  /** Subject alias that claimed a message routed here. */
  matched_subject?: string;
  /** Needs routing and no rule claims it: the connected project its content and history point to. */
  suggestion?: AmsEmailRouteSuggestion;
}

/** A suggested project for an unrouted message. Never routes anything by itself. */
export interface AmsEmailRouteSuggestion {
  /** The one project the evidence points to; absent when projects are tied. */
  project?: AmsEmailProjectRef;
  /** What in the message ties it to the project. */
  reasons?: string[];
  /** Projects equally supported when no single one leads. */
  tied?: AmsEmailProjectRef[];
  /** A routing rule that would send mail like this to the suggested project. */
  rule_hint?: { field: AmsRoutingField; match_mode: AmsRoutingMatchMode; match_value: string };
}

export interface AmsEmailRuleConflict {
  /** Matching projects the caller may see, with their matching rules. */
  matches: AmsEmailRuleMatch[];
  /** Some matching projects are not visible to the caller. */
  hidden_projects?: boolean;
}

export interface AmsEmailRuleMatch {
  project: AmsEmailProjectRef;
  rules: {
    id: string;
    field?: AmsRoutingField;
    match_mode?: AmsRoutingMatchMode;
    match_value?: string;
    subject_aliases?: string[];
    claim_all: boolean;
  }[];
}

export interface AmsStoredEmailMessage {
  id: string;
  mail_source_id: string;
  subject: string;
  from: string;
  recipients: string;
  delivered_to: string;
  sent_at?: string;
  received_at?: string;
  excerpt: string;
  body: string;
  routing: AmsEmailMessageRouting;
}

export interface AmsStoredEmailMessages {
  /** false: no mail-source connection; the project uses the legacy inbox. */
  shared_inbox: boolean;
  messages: AmsStoredEmailMessage[];
}

export interface AmsEmailImportResult {
  status: 'imported' | 'already_imported' | 'confirmation_required';
  message: AmsStoredEmailMessage;
  route_id?: string;
  signal_created: boolean;
  application_key?: string;
  environment?: string;
}

const storedEmailKey = (projectId: string) => ['projects', projectId, 'ams', 'email', 'stored'] as const;

/** Stored messages (review=false) or only those needing routing (review=true). */
export function useAmsStoredEmailMessages(projectId: string, review = false, enabled = true) {
  return useQuery({
    queryKey: [...storedEmailKey(projectId), review ? 'review' : 'all'],
    queryFn: ({ signal }) =>
      apiClient<AmsStoredEmailMessages>(`/projects/${projectId}/ams/email/${review ? 'review' : 'messages'}`, { signal }),
    enabled: enabled && !!projectId,
    staleTime: 30_000,
  });
}

/** Imports a stored message into projectId — always the explicit target. */
export function useImportAmsEmailMessage(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiClient<AmsEmailImportResult>(`/projects/${projectId}/ams/email/messages/${messageId}/import`, {
        method: 'POST',
        body: JSON.stringify({ confirm: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storedEmailKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'monitoring'] });
    },
  });
}

/**
 * Removes a stored message from projectId's Needs Routing queue (×). The
 * message, its routing and the mailbox are untouched and nothing is imported;
 * it only leaves this project's review queue.
 */
export function useDismissAmsEmailReviewMessage(projectId: string) {
  const queryClient = useQueryClient();
  const reviewKey = [...storedEmailKey(projectId), 'review'];
  return useMutation({
    mutationFn: (messageId: string) =>
      apiClient<{ dismissed: boolean }>(`/projects/${projectId}/ams/email/messages/${messageId}/dismiss`, {
        method: 'POST',
      }),
    onSuccess: (_res, messageId) => {
      queryClient.setQueryData<AmsStoredEmailMessages>(reviewKey, (prev) =>
        prev ? { ...prev, messages: prev.messages.filter((m) => m.id !== messageId) } : prev,
      );
      queryClient.invalidateQueries({ queryKey: reviewKey });
    },
  });
}

// Shared inbox configuration: the project's mail-source connection and the
// routing rules that claim the source's mail for it.

export type AmsRoutingField = 'recipient' | 'delivered_to' | 'sender' | 'subject';
export type AmsRoutingMatchMode = 'equals' | 'prefix' | 'contains';

export interface AmsRoutingRule {
  id: string;
  field?: AmsRoutingField;
  match_mode?: AmsRoutingMatchMode;
  match_value?: string;
  /** Extra subject phrases that must also match. Empty means the field rule alone. */
  subject_aliases?: string[];
  claim_all: boolean;
  enabled: boolean;
  created_by?: string;
  created_at: string;
}

export interface AmsRoutingRuleInput {
  field?: AmsRoutingField;
  match_mode?: AmsRoutingMatchMode;
  match_value?: string;
  subject_aliases?: string[];
  claim_all?: boolean;
  enabled?: boolean;
}

export interface AmsMailSource {
  id: string;
  imap_host: string;
  imap_port: number;
  use_tls: boolean;
  username: string;
  folder: string;
  enabled: boolean;
  has_password: boolean;
  last_fetch_at?: string;
  last_error?: string;
  /** Enabled project connections, this project included. */
  connections: number;
}

export interface AmsSharedInboxConnection {
  connected: boolean;
  mailbox_id?: string;
  enabled: boolean;
  source?: AmsMailSource;
  rules: AmsRoutingRule[];
  warnings: string[];
}

export interface AmsSharedInboxState {
  mode: 'off' | 'shadow' | 'on';
  /** false: ENSO_SHARED_INBOX is off on the server; nothing else is set. */
  enabled: boolean;
  connection?: AmsSharedInboxConnection;
  can_edit?: boolean;
}

export interface AmsMailSourceInput {
  imap_host: string;
  imap_port?: number;
  use_tls?: boolean;
  username: string;
  /** Blank reuses this project's saved inbox password. */
  password?: string;
  folder?: string;
}

const sharedInboxKey = (projectId: string) => ['projects', projectId, 'ams', 'email', 'shared-inbox'] as const;

export function useAmsSharedInbox(projectId: string, enabled = true) {
  return useQuery({
    queryKey: sharedInboxKey(projectId),
    queryFn: ({ signal }) => apiClient<AmsSharedInboxState>(`/projects/${projectId}/ams/email/shared-inbox`, { signal }),
    enabled: enabled && !!projectId,
    staleTime: 30_000,
  });
}

export interface AmsMailSourceProject {
  project_id: string;
  project_name: string;
}

/** Enabled projects the viewer may access that are connected to this project's mail source; [] when not connected. */
export function useAmsSharedInboxProjects(projectId: string, enabled = true) {
  return useQuery({
    queryKey: [...sharedInboxKey(projectId), 'projects'] as const,
    queryFn: ({ signal }) =>
      apiClient<{ projects: AmsMailSourceProject[] }>(`/projects/${projectId}/ams/email/shared-inbox/projects`, { signal }),
    enabled: enabled && !!projectId,
    staleTime: 30_000,
    select: (d) => d.projects ?? [],
  });
}

/** Connect, disconnect and rule changes; each refreshes the configuration and the stored messages. */
export function useAmsSharedInboxMutations(projectId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: sharedInboxKey(projectId) });
    queryClient.invalidateQueries({ queryKey: storedEmailKey(projectId) });
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'email-mailbox'] });
  };
  const base = `/projects/${projectId}/ams/email`;
  return {
    connect: useMutation({
      mutationFn: (body: AmsMailSourceInput) =>
        apiClient<AmsSharedInboxConnection>(`${base}/shared-inbox`, { method: 'PUT', body: JSON.stringify(body) }),
      onSuccess,
    }),
    disconnect: useMutation({
      mutationFn: () => apiClient<{ connected: boolean }>(`${base}/shared-inbox`, { method: 'DELETE' }),
      onSuccess,
    }),
    createRule: useMutation({
      mutationFn: (body: AmsRoutingRuleInput) =>
        apiClient<AmsRoutingRule>(`${base}/routing-rules`, { method: 'POST', body: JSON.stringify(body) }),
      onSuccess,
    }),
    updateRule: useMutation({
      mutationFn: ({ id, ...body }: AmsRoutingRuleInput & { id: string }) =>
        apiClient<AmsRoutingRule>(`${base}/routing-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      onSuccess,
    }),
    deleteRule: useMutation({
      mutationFn: (id: string) => apiClient<{ deleted: boolean }>(`${base}/routing-rules/${id}`, { method: 'DELETE' }),
      onSuccess,
    }),
  };
}

export function useRegisterMuleSoftAmsRoutes(projectId: string) {
  return useMutation({
    mutationFn: () =>
      apiClient<{ routes: unknown[] }>(`/projects/${projectId}/ams/email-mailbox/register-mulesoft-routes`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
  });
}

export function useProjectAmsApplicationKeys(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'application-keys'],
    queryFn: async () => {
      const data = await apiClient<{ application_keys?: string[] }>(
        `/projects/${projectId}/ams/application-keys`,
      );
      return data?.application_keys ?? [];
    },
    enabled: Boolean(projectId),
  });
}

/** A registered application × environment (ams_app_environments). */
export interface AmsAppEnvironmentRef {
  application_key: string;
  environment: string;
}

/** Registered application × environment pairs — the only identities an email alert binding may name. */
export function useAmsAppEnvironments(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'app-environments'],
    queryFn: async () =>
      (await apiClient<AmsAppEnvironmentRef[] | null>(`/projects/${projectId}/ams/app-environments`)) ?? [],
    enabled: Boolean(projectId),
  });
}

/**
 * Registers an application × environment pair. Sends only the pair; the
 * backend normalizes the environment and leaves metrics fields at defaults.
 */
export function useRegisterAmsAppEnvironment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AmsAppEnvironmentRef) =>
      apiClient<AmsAppEnvironmentRef>(`/projects/${projectId}/ams/app-environments`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'app-environments'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'application-keys'] });
    },
  });
}

export type AmsEmailAlertBindingMatchMode = 'subject_exact' | 'subject_contains';

/** Binds an inbox alert pattern to a registered application × environment. */
export interface AmsEmailAlertBinding {
  id: string;
  project_id: string;
  name: string;
  match_mode: AmsEmailAlertBindingMatchMode;
  match_value: string;
  application_key: string;
  environment: string;
  enabled: boolean;
  /** False when the bound application × environment is no longer registered; it then never matches. */
  valid: boolean;
  created_at: string;
  updated_at: string;
}

export interface AmsEmailAlertBindingInput {
  name?: string;
  match_mode: AmsEmailAlertBindingMatchMode;
  match_value: string;
  application_key: string;
  environment: string;
  enabled?: boolean;
}

const emailAlertBindingsKey = (projectId: string) => ['projects', projectId, 'ams', 'email-alert-bindings'];

export function useAmsEmailAlertBindings(projectId: string, enabled = true) {
  return useQuery({
    queryKey: emailAlertBindingsKey(projectId),
    queryFn: async () =>
      (await apiClient<AmsEmailAlertBinding[] | null>(`/projects/${projectId}/ams/email-alert-bindings`)) ?? [],
    enabled: Boolean(projectId) && enabled,
    retry: false,
  });
}

export function useSaveAmsEmailAlertBinding(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AmsEmailAlertBindingInput & { id?: string }) =>
      apiClient<AmsEmailAlertBinding>(
        id
          ? `/projects/${projectId}/ams/email-alert-bindings/${encodeURIComponent(id)}`
          : `/projects/${projectId}/ams/email-alert-bindings`,
        { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailAlertBindingsKey(projectId) });
    },
  });
}

export function useDeleteAmsEmailAlertBinding(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient(`/projects/${projectId}/ams/email-alert-bindings/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailAlertBindingsKey(projectId) });
    },
  });
}

export function useAmsLogIngests(projectId: string, limit = 10) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'log-ingests', limit],
    queryFn: async () => {
      const { data } = await apiClientWithMeta<AmsLogIngestRecord[] | null>(
        `/projects/${projectId}/ams/log-ingests?limit=${limit}`,
      );
      return data ?? [];
    },
    enabled: Boolean(projectId),
  });
}

/** Delete a runtime log upload and the incidents it created (409 when engineering work exists). */
export function useDeleteAmsLogIngest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ingestId: string) =>
      apiClient<AmsLogIngestDeleteResult>(`/projects/${projectId}/ams/log-ingests/${ingestId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'log-ingests'] });
      queryClient.invalidateQueries({ queryKey: ['ams', 'inbox'] });
    },
  });
}

export function useBatchVerifyAmsIncidents(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentIds: string[]) =>
      apiClient<{ verified: string[]; failed: Array<{ incident_id?: string; error?: string }> }>(
        `/projects/${projectId}/ams/incidents/batch-verify`,
        { method: 'POST', body: JSON.stringify({ incident_ids: incidentIds }) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams'] });
    },
  });
}

/**
 * Removes an incident from the project's incident queues (×). The incident,
 * its events, investigation history and attachments are kept server-side.
 */
export function useDismissAmsIncident(projectId: string) {
  const queryClient = useQueryClient();
  const key = ['projects', projectId, 'ams', 'incidents'];
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClient<{ dismissed: boolean }>(`/projects/${projectId}/ams/incidents/${incidentId}/dismiss`, {
        method: 'POST',
      }),
    onSuccess: (_res, incidentId) => {
      queryClient.setQueryData<{ items: AmsIncident[]; dataGeneration?: unknown }>(key, (prev) =>
        prev ? { ...prev, items: prev.items.filter((i) => i.id !== incidentId) } : prev,
      );
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useProjectAmsIncidents(projectId: string) {
  const query = useQuery({
    queryKey: ['projects', projectId, 'ams', 'incidents'],
    queryFn: async ({ signal }) => {
      const { data, dataGeneration } = await apiClientWithMeta<AmsIncident[] | null>(
        `/projects/${projectId}/ams/incidents`,
        { timeoutMs: 60_000, signal },
      );
      return { items: data ?? [], dataGeneration };
    },
    enabled: Boolean(projectId),
    // List is compact; poll for new IMAP extracts without hammering the API.
    refetchInterval: 30_000,
  });
  return {
    ...query,
    data: query.data?.items,
    dataGeneration: query.data?.dataGeneration,
  };
}

/**
 * Runtime-log incidents, optionally only those created by one log upload.
 * Keyed under ['projects', id, 'ams', 'incidents'] so ingest/delete invalidations refresh it.
 */
export function useRuntimeLogIncidents(projectId: string, logIngestId?: string | null, limit = 500) {
  const ingest = logIngestId || '';
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'incidents', 'runtime_log', ingest, limit],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ source: 'runtime_log', limit: String(limit) });
      if (ingest) qs.set('log_ingest', ingest);
      const { data } = await apiClientWithMeta<AmsIncident[] | null>(
        `/projects/${projectId}/ams/incidents?${qs.toString()}`,
        { timeoutMs: 60_000, signal },
      );
      return data ?? [];
    },
    enabled: Boolean(projectId) && logIngestId !== null,
    refetchInterval: 30_000,
  });
}

/** Full incident payload (RCA report, evidence graph) for detail panels — list endpoint trims heavy fields. */
export function useProjectAmsIncident(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'incidents', incidentId],
    queryFn: ({ signal }) =>
      apiClient<AmsIncident>(`/projects/${projectId}/ams/incidents/${incidentId}`, { signal }),
    enabled: Boolean(projectId && incidentId),
    staleTime: 60_000,
    refetchInterval: (query) => alertSummaryPollInterval(query.state.data),
  });
}

/** Authoritative AMS parent lifecycle (Gate 3). Endpoint returns {success, workflow, history} — not {data}. */
export interface AmsWorkflow {
  workflow_id: string;
  project_id: string;
  application_id: string;
  incident_id: string;
  correlation_id: string;
  stage: string;
  mode: string;
  decision_id?: string;
  change_request_id?: string;
  execution_ref?: string;
  investigation_attempt?: number;
  execution_attempt?: number;
  needs_human?: boolean;
  version: number;
  created_at?: string;
  updated_at?: string;
}

export interface AmsWorkflowTransition {
  id: string;
  workflow_id: string;
  from_stage: string;
  to_stage: string;
  command: string;
  command_id: string;
  actor: string;
  mode?: string;
  reason?: string;
  version_before?: number;
  version_after?: number;
  result?: string;
  created_at?: string;
}

export interface AmsWorkflowEnvelope {
  success: boolean;
  workflow: AmsWorkflow;
  history?: AmsWorkflowTransition[];
  error?: { message?: string };
}

export function useAmsIncidentWorkflow(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
    queryFn: async (): Promise<AmsWorkflowEnvelope> => {
      const res = await fetchWithTimeout(
        `${API_BASE}/projects/${projectId}/ams/incidents/${incidentId}/workflow`,
        {
          headers: {
            Accept: 'application/json',
            ...authHeaders(),
          },
          timeoutMs: 30_000,
        },
      );
      handleUnauthorizedResponse(res.status, `/projects/${projectId}/ams/incidents/${incidentId}/workflow`);
      const body = (await res.json().catch(() => ({}))) as AmsWorkflowEnvelope;
      if (res.status === 404) {
        throw new ApiError(body.error?.message || 'workflow not found', 404);
      }
      if (!res.ok || body.success === false) {
        throw new ApiError(body.error?.message || `workflow request failed (${res.status})`, res.status);
      }
      if (!body.workflow) {
        throw new Error('workflow payload missing');
      }
      return body;
    },
    enabled: Boolean(projectId && incidentId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const stage = query.state.data?.workflow?.stage;
      if (!stage || String(stage).toUpperCase() === 'CLOSED') return false;
      return refetchWhenActive(query, {
        intervalMs: amsWorkflowNeedsFastPoll(stage) ? 8_000 : 20_000,
      });
    },
    retry: (_, error) => !(error instanceof Error && /not found/i.test(error.message)),
  });
}

export interface AmsLifecycleOutcome {
  id: string;
  project_id: string;
  application_id?: string;
  incident_id: string;
  workflow_id?: string;
  decision_id?: string;
  change_request_id?: string;
  outcome_kind: string;
  resolution: string;
  summary: string;
  evidence?: Record<string, unknown>;
  workflow_stage_at_record: string;
  created_at: string;
}

export function useAmsLifecycleOutcomes(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'lifecycle', 'outcomes'],
    queryFn: async (): Promise<AmsLifecycleOutcome[]> => {
      const res = await fetchWithTimeout(
        `${API_BASE}/projects/${projectId}/ams/incidents/${incidentId}/lifecycle/outcomes`,
        {
          headers: { Accept: 'application/json', ...authHeaders() },
          timeoutMs: 30_000,
        },
      );
      handleUnauthorizedResponse(res.status, `/projects/${projectId}/ams/incidents/${incidentId}/lifecycle/outcomes`);
      const body = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        outcomes?: AmsLifecycleOutcome[];
        error?: { message?: string };
      };
      if (!res.ok || body.success === false) {
        throw new ApiError(body.error?.message || `lifecycle outcomes failed (${res.status})`, res.status);
      }
      return body.outcomes ?? [];
    },
    enabled: Boolean(projectId && incidentId),
    staleTime: 8_000,
    refetchInterval: (query) => refetchWhenActive(query, { intervalMs: 15_000 }),
  });
}

export function useReportAmsExecutionOutcome(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      incidentId,
      success,
      reason,
      recoveryTarget,
    }: {
      incidentId: string;
      success: boolean;
      reason?: string;
      recoveryTarget?: string;
    }) =>
      apiClientRawWorkflow(`/projects/${projectId}/ams/incidents/${incidentId}/lifecycle/execution-outcome`, {
        method: 'POST',
        body: JSON.stringify({
          success,
          reason,
          recovery_target: recoveryTarget,
        }),
      }),
    onSuccess: (_data, { incidentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'lifecycle', 'outcomes'],
      });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
    },
  });
}

export function useReportAmsVerificationOutcome(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      incidentId,
      passed,
      reason,
      recoveryTarget,
    }: {
      incidentId: string;
      passed: boolean;
      reason?: string;
      recoveryTarget?: string;
    }) =>
      apiClientRawWorkflow(
        `/projects/${projectId}/ams/incidents/${incidentId}/lifecycle/verification-outcome`,
        {
          method: 'POST',
          body: JSON.stringify({
            passed,
            success: passed,
            reason,
            recovery_target: recoveryTarget,
          }),
        },
      ),
    onSuccess: (_data, { incidentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'lifecycle', 'outcomes'],
      });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
    },
  });
}

/** POST helper for lifecycle endpoints that return {success, workflow} not {data}. */
async function apiClientRawWorkflow(
  endpoint: string,
  options: TimedRequestInit = {},
): Promise<{ workflow?: AmsWorkflow }> {
  const res = await fetchWithTimeout(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(options.headers as Record<string, string>),
    },
  });
  handleUnauthorizedResponse(res.status, endpoint);
  const body = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    workflow?: AmsWorkflow;
    error?: { message?: string };
  };
  if (!res.ok || body.success === false) {
    throw new ApiError(body.error?.message || `request failed (${res.status})`, res.status);
  }
  return body;
}

export function useAnalyzeAmsIncident(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClient<{ message: string; incident_id: string }>(
        `/projects/${projectId}/ams/incidents/${incidentId}/analyze`,
        { method: 'POST', timeoutMs: 120_000 }
      ),
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['ams', 'inbox'] });
      queryClient.invalidateQueries({ queryKey: skillExecutionsKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'incidents', incidentId, 'dashboard'] });
    },
  });
}

/**
 * Re-runs only the AI alert analysis (one model call). Unlike
 * useAnalyzeAmsIncident it does not rebuild the investigation or re-evaluate
 * the mission decision, which starts a full Engineering Intelligence run.
 */
export function useRefreshAlertAnalysis(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClient<unknown>(`/projects/${projectId}/ams/incidents/${incidentId}/alert-analysis`, {
        method: 'POST',
        timeoutMs: 180_000,
      }),
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
    },
  });
}

export function useVerifyAmsIncident(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClient<AmsIncident>(`/projects/${projectId}/ams/incidents/${incidentId}/verify`, {
        method: 'POST',
      }),
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'incidents', incidentId, 'dashboard'] });
    },
  });
}

export function usePromoteAmsIncident(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, stage }: { incidentId: string; stage: 'customize' | 'enhance' }) =>
      apiClient<AmsIncident>(`/projects/${projectId}/ams/incidents/${incidentId}/promote`, {
        method: 'POST',
        body: JSON.stringify({ stage }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
    },
  });
}

export function useIngestAmsEmailIncident(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      subject: string;
      from_address?: string;
      to_address?: string;
      severity?: string;
      application_key?: string;
      transaction_id?: string;
      summary?: string;
      body?: string;
      received_at?: string;
      frequency?: string;
      cc_addresses?: string;
      auto_analyze?: boolean;
    }) =>
      apiClient<AmsIncident>(`/projects/${projectId}/ams/incidents`, {
        method: 'POST',
        body: JSON.stringify({
          source: 'email',
          title: body.subject,
          subject: body.subject,
          from_address: body.from_address,
          to_address: body.to_address,
          severity: body.severity || 'medium',
          application_key: body.application_key,
          transaction_id: body.transaction_id,
          summary: body.summary || body.body,
          body: body.body,
          auto_analyze: body.auto_analyze !== false,
          payload: {
            received_at: body.received_at,
            frequency: body.frequency,
            cc_addresses: body.cc_addresses,
            ingestion_mode: 'manual',
          },
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'skills'] });
      queryClient.invalidateQueries({ queryKey: ['ams', 'inbox'] });
    },
  });
}

export type AmsLogIngestInput = {
  file: File;
  application_key?: string;
  environment?: string;
  source_kind?: string;
};

export function useIngestAmsLogs(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: File | AmsLogIngestInput) => {
      const file = input instanceof File ? input : input.file;
      const application_key = input instanceof File ? '' : (input.application_key || '');
      const environment = input instanceof File ? '' : (input.environment || '');
      const source_kind = input instanceof File ? 'upload' : (input.source_kind || 'upload');
      const base = import.meta.env.VITE_API_URL || '/api/v1';
      const form = new FormData();
      form.append('file', file);
      if (application_key) form.append('application_key', application_key);
      if (environment) form.append('environment', environment);
      if (source_kind) form.append('source_kind', source_kind);
      const res = await fetch(`${base}/projects/${projectId}/ams/logs/ingest`, {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: {
          ...authHeaders(),
        },
      });
      handleUnauthorizedResponse(res.status, `/projects/${projectId}/ams/logs/ingest`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new ApiError(
          (err as { error?: { message?: string } })?.error?.message || res.statusText,
          res.status,
        );
      }
      const json = await res.json();
      return (json.data ?? json) as AmsLogIngestResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'log-ingests'] });
      queryClient.invalidateQueries({ queryKey: ['ams', 'inbox'] });
    },
  });
}

export type AmsLogIngestConfig = {
  project_id: string;
  webhook_path: string;
  has_token: boolean;
  token_prefix?: string;
  created_at?: string | null;
  last_used_at?: string | null;
  token_header: string;
  sample_curl_hint?: string;
};

export type AmsLogIngestTokenRotate = AmsLogIngestConfig & {
  token: string;
};

export function useAmsLogIngestConfig(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'logs', 'ingest-config'],
    queryFn: ({ signal }) => apiClient<AmsLogIngestConfig>(`/projects/${projectId}/ams/logs/ingest-config`, { signal }),
    enabled: Boolean(projectId),
  });
}

export function useRotateAmsLogIngestToken(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<AmsLogIngestTokenRotate>(`/projects/${projectId}/ams/logs/ingest-token`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'logs', 'ingest-config'],
      });
    },
  });
}

export type AmsLogHTTPSource = {
  id: string;
  project_id: string;
  name: string;
  url: string;
  has_auth?: boolean;
  auth_header_name?: string;
  application_key: string;
  environment: string;
  enabled: boolean;
  lookback_minutes: number;
  last_pulled_at?: string | null;
  last_pull_status?: string;
  last_pull_error?: string;
  last_incidents_created?: number;
  created_at?: string;
  updated_at?: string;
};

export type AmsLogHTTPSourceInput = {
  name?: string;
  url: string;
  auth_bearer?: string;
  auth_header_name?: string;
  auth_header_value?: string;
  clear_auth?: boolean;
  application_key: string;
  environment: string;
  enabled?: boolean;
  lookback_minutes?: number;
};

export function useAmsLogHTTPSources(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'ams', 'logs', 'http-sources'],
    queryFn: async () => {
      const data = await apiClient<AmsLogHTTPSource[] | null>(
        `/projects/${projectId}/ams/logs/http-sources`,
      );
      return data ?? [];
    },
    enabled: Boolean(projectId),
  });
}

export function useCreateAmsLogHTTPSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AmsLogHTTPSourceInput) =>
      apiClient<AmsLogHTTPSource>(`/projects/${projectId}/ams/logs/http-sources`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'logs', 'http-sources'],
      });
    },
  });
}

export function useUpdateAmsLogHTTPSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, body }: { sourceId: string; body: AmsLogHTTPSourceInput }) =>
      apiClient<AmsLogHTTPSource>(`/projects/${projectId}/ams/logs/http-sources/${sourceId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'logs', 'http-sources'],
      });
    },
  });
}

export function useDeleteAmsLogHTTPSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      apiClient(`/projects/${projectId}/ams/logs/http-sources/${sourceId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'logs', 'http-sources'],
      });
    },
  });
}

export function usePullAmsLogHTTPSource(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      apiClient<AmsLogIngestResult>(`/projects/${projectId}/ams/logs/http-sources/${sourceId}/pull`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams', 'log-ingests'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'logs', 'http-sources'],
      });
    },
  });
}

// --- Baseline & environment promotions (Wave C) ---

export interface BaselineTestCase {
  id: string;
  kind: string;
  title: string;
  description?: string;
  required?: boolean;
  runner_hint?: string;
  target_paths?: string[];
  expected?: string;
  fingerprint?: string;
}

export interface BaselineTestPlan {
  schema?: string;
  id: string;
  generated_at?: string;
  objective?: string;
  cases?: BaselineTestCase[];
  locked?: boolean;
}

export interface BaselineTestCaseResult {
  case_id: string;
  verdict: string;
  log?: string;
  at?: string;
}

export interface BaselineTestReport {
  schema?: string;
  plan_id?: string;
  baseline_id?: string;
  overall: string;
  required_pass?: number;
  required_fail?: number;
  optional_fail?: number;
  results?: BaselineTestCaseResult[];
  executed_at?: string;
  notes?: string;
}

export interface ProjectBaseline {
  id: string;
  project_id: string;
  snapshot_id?: string;
  version: string;
  label: string;
  metrics: {
    file_count: number;
    components: number;
    services: number;
    datastores: number;
    kg_nodes: number;
    kg_edges: number;
    open_issues: number;
    by_language?: Record<string, number>;
  };
  test_plan?: BaselineTestPlan | null;
  is_active: boolean;
  locked_at: string;
}

export interface BaselineTestCase {
  id: string;
  kind: string;
  title: string;
  description?: string;
  required?: boolean;
  runner_hint?: string;
  target_paths?: string[];
  expected?: string;
  fingerprint?: string;
}

export interface BaselineTestPlan {
  schema?: string;
  id: string;
  generated_at?: string;
  objective?: string;
  cases?: BaselineTestCase[];
  locked?: boolean;
}

export interface BaselineTestCaseResult {
  case_id: string;
  verdict: string;
  log?: string;
  at?: string;
}

export interface BaselineTestReport {
  schema?: string;
  plan_id?: string;
  baseline_id?: string;
  overall: string;
  required_pass?: number;
  required_fail?: number;
  optional_fail?: number;
  results?: BaselineTestCaseResult[];
  executed_at?: string;
  notes?: string;
}

export interface BaselineCompareResult {
  baseline: ProjectBaseline;
  live: ProjectBaseline['metrics'];
  deltas: Record<string, number>;
  generated_at: string;
}

export interface EnvironmentPromotion {
  id: string;
  project_id: string;
  change_request_id?: string;
  from_stage: string;
  to_stage: string;
  status: string;
  check_results: Record<string, unknown>[];
  notes?: string;
  created_at: string;
  completed_at?: string;
}

export function useProjectBaseline(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'baseline'],
    queryFn: async () => {
      const data = await apiClient<ProjectBaseline | null>(`/projects/${projectId}/baseline`);
      return data ?? null;
    },
    enabled: !!projectId,
  });
}

export function useLockBaseline(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body?: { label?: string }) =>
      apiClient<ProjectBaseline>(`/projects/${projectId}/baseline`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'baseline'] });
      queryClient.invalidateQueries({ queryKey: knowledgeSnapshotsKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'baseline', 'compare'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'baseline', 'test-plan'] });
    },
  });
}

export function useBaselineCompare(projectId: string, enabled = true) {
  return useQuery({
    queryKey: ['projects', projectId, 'baseline', 'compare'],
    queryFn: ({ signal }) => apiClient<BaselineCompareResult>(`/projects/${projectId}/baseline/compare`, { signal }),
    enabled: !!projectId && enabled,
    retry: false,
  });
}

export function useBaselineTestPlanPreview(projectId: string, enabled = true) {
  return useQuery({
    queryKey: ['projects', projectId, 'baseline', 'test-plan'],
    queryFn: ({ signal }) => apiClient<BaselineTestPlan>(`/projects/${projectId}/baseline/test-plan`, { signal }),
    enabled: Boolean(projectId) && enabled,
    staleTime: 30_000,
  });
}

export function useExecuteBaselineTestPlan(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body?: { working_tree?: string; use_draft?: boolean }) =>
      apiClient<BaselineTestReport>(`/projects/${projectId}/baseline/test-plan/execute`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'baseline'] });
    },
  });
}

export function useEnvironmentPromotions(projectId: string) {
  const query = useQuery({
    queryKey: ['projects', projectId, 'promotions'],
    queryFn: async () => {
      const { data, dataGeneration } = await apiClientWithMeta<EnvironmentPromotion[] | null>(
        `/projects/${projectId}/promotions`,
      );
      return { items: data ?? [], dataGeneration };
    },
    enabled: !!projectId,
  });
  return {
    ...query,
    data: query.data?.items,
    dataGeneration: query.data?.dataGeneration,
  };
}

export function usePromoteEnvironment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { to_stage: 'test' | 'prod'; change_request_id?: string; notes?: string }) =>
      apiClient<EnvironmentPromotion>(`/projects/${projectId}/promotions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'promotions'] });
    },
  });
}

export function useCreateIncidentChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClient<{ change_request_id: string }>(
        `/projects/${projectId}/ams/incidents/${incidentId}/change-request`,
        { method: 'POST' }
      ),
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incidents(projectId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'change-requests'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
    },
  });
}

export function useMissionDecision(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: amsQueryKeys.missionDecision(projectId, incidentId ?? ''),
    queryFn: async () => {
      try {
        const { data } = await apiClientWithMeta<MissionDecision>(
          `/projects/${projectId}/ams/incidents/${incidentId}/mission-decision`,
        );
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) return null;
        throw err;
      }
    },
    enabled: Boolean(projectId && incidentId),
    retry: false,
  });
}

export function useEvaluateMissionDecision(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClientWithMeta<MissionDecision>(
        `/projects/${projectId}/ams/incidents/${incidentId}/mission-decision/evaluate`,
        { method: 'POST' },
      ).then((r) => r.data),
    onSuccess: (_data, incidentId) => {
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.missionDecision(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
    },
  });
}

export function useImplementationPlan(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: amsQueryKeys.implementationPlan(projectId, incidentId ?? ''),
    queryFn: async () => {
      try {
        const { data } = await apiClientWithMeta<import('../lib/engineeringPlan').ImplementationPlanResult>(
          `/projects/${projectId}/ams/incidents/${incidentId}/implementation-plan`,
        );
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) return null;
        throw err;
      }
    },
    enabled: Boolean(projectId && incidentId),
    retry: false,
  });
}

export function useGenerateImplementationPlan(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (incidentId: string) =>
      apiClientWithMeta<import('../lib/engineeringPlan').ImplementationPlanResult>(
        `/projects/${projectId}/ams/incidents/${incidentId}/implementation-plan/generate`,
        { method: 'POST', timeoutMs: 120_000 },
      ).then((r) => r.data),
    onSuccess: (data, incidentId) => {
      queryClient.setQueryData(amsQueryKeys.implementationPlan(projectId, incidentId), data);
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.implementationPlan(projectId, incidentId) });
    },
  });
}

export function useApplyMissionDecision(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      incidentId,
      body,
    }: {
      incidentId: string;
      body?: ApplyMissionDecisionInput;
    }) =>
      apiClientWithMeta<MissionDecision>(
        `/projects/${projectId}/ams/incidents/${incidentId}/mission-decision/apply`,
        {
          method: 'POST',
          body: JSON.stringify(body ?? { approved_by: 'operator', approved: true, force: true }),
        },
      ).then((r) => r.data),
    onSuccess: (_data, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.missionDecision(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incidents(projectId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.operationalExecution(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: ['engineering'] });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
    },
  });
}

/** User-confirmed resolution ownership of an incident (rca_ownership_decisions). */
export function useRcaOwnership(projectId: string, incidentId: string | null | undefined) {
  return useQuery({
    queryKey: amsQueryKeys.rcaOwnership(projectId, incidentId ?? ''),
    queryFn: async () =>
      (await apiClientWithMeta<RcaOwnershipRecord>(`/projects/${projectId}/ams/incidents/${incidentId}/rca-ownership`)).data,
    enabled: Boolean(projectId && incidentId),
    retry: false,
    // A running record is never fresh: remounting the incident (or returning
    // to the tab) after navigating away fetches again instead of showing the
    // cached "investigating" state. Settled records keep the default 30s.
    staleTime: (q) => (q.state.data?.engine_status === 'running' ? 0 : 30_000),
    // Follow a running RCA Engine investigation until it completes or fails;
    // a failed poll backs off instead of stopping.
    refetchInterval: (q) =>
      refetchWhenActive(q, { intervalMs: 3000, shouldPoll: (d) => d?.engine_status === 'running' }),
  });
}

/**
 * Records that the project's uploaded code is what was running when the
 * incident happened. The backend derives the application and environment and
 * re-runs the RCA Engine on the recorded deployment.
 */
export async function markUploadDeployed(projectId: string, incidentId: string) {
  return apiClientWithMeta<{ application_key: string; environment: string; snapshot_revision: string }>(
    `/projects/${projectId}/ams/incidents/${incidentId}/mark-upload-deployed`,
    { method: 'POST', body: '{}' },
  );
}

/** What PUT application-attribution reports back. */
export interface IncidentAttributionResult {
  attribution: { previous: { application_key: string; environment: string }; current: { application_key: string; environment: string } };
  /** The deployment the incident now resolves to. Resolved is a recorded revision, not yet verified. */
  deployment: { status: string; reason?: string; revision_id?: string; commit_sha?: string } | null;
  rca_rerun: { scheduled: boolean; run_id?: string; reason?: string; error?: string; retry?: string };
}

/**
 * Attributes an existing incident to a registered application environment.
 * Attribution names the application; it does not verify a deployed revision.
 * The backend re-runs the RCA only when a recorded deployment now covers it.
 */
export async function attributeIncidentApplication(
  projectId: string,
  incidentId: string,
  body: { application_key: string; environment: string; reason: string },
) {
  return apiClient<IncidentAttributionResult>(
    `/projects/${projectId}/ams/incidents/${incidentId}/application-attribution`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

/** Re-runs the RCA Engine for an incident. The user's ownership decision is kept. */
export async function rerunRcaEngine(projectId: string, incidentId: string) {
  return apiClientWithMeta<{ run_id: string; engine_status: string }>(
    `/projects/${projectId}/ams/incidents/${incidentId}/rca-engine/run`,
    { method: 'POST', timeoutMs: 120_000 },
  );
}

/**
 * Records the user's ownership choice, with any Engineering context (answers
 * keyed by the backend's requirement IDs); the backend re-evaluates the
 * mission decision.
 */
export function useConfirmRcaOwnership(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // reviewedRunId is the RCA run the user was shown; the backend refuses
    // the decision (409 RCA_CHANGED) when the RCA has been replaced since.
    mutationFn: ({ incidentId, owner, reason, engineeringContext, escalated, reviewedRunId }: { incidentId: string; owner: RcaOwner; reason?: string; engineeringContext?: Record<string, string>; escalated?: boolean; reviewedRunId?: string }) =>
      apiClientWithMeta<{ ownership: RcaOwnershipRecord; mission_decision?: MissionDecision }>(
        `/projects/${projectId}/ams/incidents/${incidentId}/rca-ownership`,
        { method: 'PUT', body: JSON.stringify({ owner, reason: reason ?? '', engineering_context: engineeringContext ?? {}, engineering_escalated: escalated === true, run_id: reviewedRunId ?? '' }) },
      ).then((r) => r.data),
    onSuccess: (data, { incidentId }) => {
      queryClient.setQueryData(amsQueryKeys.rcaOwnership(projectId, incidentId), data.ownership);
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.missionDecision(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
    },
  });
}

export function useCloseOperationalEvent(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, resolution }: { incidentId: string; resolution?: string }) =>
      apiClientWithMeta<AmsIncident>(
        `/projects/${projectId}/ams/incidents/${incidentId}/close`,
        {
          method: 'POST',
          body: JSON.stringify({ resolution: resolution ?? 'Closed by operator' }),
        },
      ).then((r) => r.data),
    onSuccess: (_data, { incidentId }) => {
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incidents(projectId) });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.incident(projectId, incidentId) });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'incidents', incidentId, 'dashboard'] });
      queryClient.invalidateQueries({ queryKey: amsQueryKeys.inbox() });
      queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ams', 'incidents', incidentId, 'workflow'],
      });
    },
  });
}

export interface ManualIngestionVerifyResult {
  incident_id: string;
  verified: boolean;
  success: boolean;
  message: string;
}

export interface ManualDropZoneCapabilities {
  allow_drop_zone_write: boolean;
  reason: string;
  connector_kind?: string;
  is_lab?: boolean;
}

export interface InvalidRecordsSourcePath {
  sftp_root: string;
  relative_path: string;
  absolute_path: string;
  source_file: string;
  folder: string;
  capabilities?: ManualDropZoneCapabilities;
}

export interface UploadCorrectedCSVResult {
  success: boolean;
  message: string;
  relative_path?: string;
  absolute_path?: string;
  bytes?: number;
  sha256?: string;
  filename?: string;
}

export interface OperatorBusinessDecisionAnswer {
  question: string;
  answer: string;
  choice?: string;
}

export interface OperatorInvalidRecordsRepairResult {
  success: boolean;
  message: string;
  target_record_type?: string;
  target_quantity?: string;
  source_file?: string;
  artifact_path?: string;
}

export function useOperationalExecution(
  projectId: string,
  incidentId: string | null | undefined,
  opts?: { enabled?: boolean; poll?: boolean },
) {
  const id = incidentId || '';
  return useQuery({
    queryKey: amsQueryKeys.operationalExecution(projectId, id),
    enabled: Boolean(projectId && id) && opts?.enabled !== false,
        refetchInterval: opts?.poll ? () => pollIntervalMs(2000) : false,
    retry: false,
    queryFn: async (): Promise<OpsExecutionSnapshot | null> => {
      try {
        return await apiClientWithMeta<OpsExecutionSnapshot>(
          `/projects/${projectId}/ams/incidents/${id}/operational-recovery/execution`,
        ).then((r) => r.data);
      } catch {
        return null;
      }
    },
  });
}

async function markAmsIncidentRead(incidentId: string): Promise<void> {
  await apiClient(`/ams/incidents/${incidentId}/read`, { method: 'POST' });
}

// @mention notifications (backend internal/collaboration). These are the only
// notification hooks — the header bell merges them with AMS/project items.
export interface CollabNotification {
  id: string;
  recipient_id: string;
  kind: string;
  payload: Record<string, unknown>;
  read_at?: string;
  created_at: string;
}

export const collabNotificationsKey = (recipient: string) =>
  ['collaboration', 'notifications', recipient] as const;

export function useCollabNotifications(recipientId: string, unreadOnly = false) {
  return useQuery({
    queryKey: collabNotificationsKey(recipientId),
    queryFn: () =>
      apiClient<{ notifications: CollabNotification[] }>(
        `/notifications?recipient_id=${encodeURIComponent(recipientId)}${unreadOnly ? '&unread=true' : ''}`,
      ),
    select: (d) => d.notifications ?? [],
    enabled: Boolean(recipientId),
    refetchInterval: () => pollIntervalMs(20_000),
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient<void>(`/notifications/${id}/read`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['collaboration', 'notifications'] });
    },
  });
}

// Mention notifications (internal/collaboration) → the same PlatformNotification
// shape the header bell already renders, rather than a second notification UI.
// The backend enriches the payload with project_id/request_id at creation
// time (see cmd/api/collaboration_handlers.go's notificationDeepLinkPayload)
// specifically so this mapper can build a real deep link instead of a bare
// "you were mentioned" toast with nowhere to go.
function buildMentionNotifications(items: CollabNotification[]): PlatformNotification[] {
  return items.map((n) => {
    const projectId = typeof n.payload.project_id === 'string' ? n.payload.project_id : undefined;
    const requestId = typeof n.payload.request_id === 'string' ? n.payload.request_id : undefined;
    const author = typeof n.payload.author_id === 'string' ? n.payload.author_id : 'Someone';
    return {
      id: `mention-${n.id}`,
      type: 'info',
      message: `${author} mentioned you in a discussion`,
      timestamp: n.created_at,
      read: Boolean(n.read_at),
      projectId,
      href: projectId && requestId
        ? `/projects/${projectId}/operate/engineering?request=${encodeURIComponent(requestId)}`
        : undefined,
    };
  });
}

/** Minimal shape of /monitoring/alerts (services/monitoring.ts holds the full types;
 * importing them here would make api.ts ↔ monitoring.ts circular). */
interface MonitoringAlertItem {
  job: { id: string; name: string; project_id: string; project_name: string };
  window: { scheduled_at: string; due_at: string; decided_at?: string; outcome: string };
}

const MONITOR_OUTCOME_TEXT: Record<string, [string, PlatformNotification['type']]> = {
  late: ['arrived late', 'warning'],
  overdue: ['is overdue', 'warning'],
  missed: ['was missed', 'error'],
  failed: ['reported a failure', 'error'],
};

function buildMonitoringNotifications(alerts: MonitoringAlertItem[]): PlatformNotification[] {
  return alerts
    .filter((a) => MONITOR_OUTCOME_TEXT[a.window.outcome])
    .map((a) => {
      const [text, type] = MONITOR_OUTCOME_TEXT[a.window.outcome];
      return {
        // One entry per run and outcome: an overdue run that turns missed is news again.
        id: `monitor-${a.job.id}-${a.window.scheduled_at}-${a.window.outcome}`,
        type,
        message: `[${a.job.project_name}] ${a.job.name} ${text}`,
        // When the problem became true (a miss is only known at the next run).
        timestamp: a.window.decided_at ?? a.window.due_at,
        read: false,
        projectId: a.job.project_id,
        href: `/monitoring?job=${encodeURIComponent(a.job.id)}`,
      };
    });
}

function useMonitoringAlertFeed() {
  return useQuery({
    queryKey: ['monitoring', 'alerts'],
    queryFn: async () => (await apiClient<MonitoringAlertItem[] | null>('/monitoring/alerts')) ?? [],
    refetchInterval: 60_000,
    retry: false,
  });
}

// Notifications — project status + live AMS inbox alerts + @mention notifications
// + monitoring alerts (late / missed / failed scheduled jobs)
export function usePlatformNotifications() {
  const { projects, isLoading: projectsLoading } = useProjects();
  const { data: inbox = [], isLoading: inboxLoading } = useAmsInbox();
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());
  // Undo a local read mark whose server update failed, so it shows unread and can be retried.
  const unmarkRead = (id: string) =>
    setReadIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  // Mentions are stored under the bare @handle, and the API only lists the
  // signed-in user's own inbox (id, email, or email local part). The free-text
  // display name was rejected with 403 on every poll, so use the login email's
  // local part — the handle colleagues type as @first.last.
  const myHandle = getMyEmail().split('@')[0] ?? '';
  const { data: mentionItems = [], isLoading: mentionsLoading } = useCollabNotifications(myHandle);
  const markMentionRead = useMarkNotificationRead();
  const { data: monitorAlerts = [] } = useMonitoringAlertFeed();

  const notifications = useMemo(() => {
    const statusItems = buildProjectNotifications(projects);
    const amsItems = buildAmsInboxNotifications(inbox);
    const mentionItemsMapped = buildMentionNotifications(mentionItems);
    const monitorItems = buildMonitoringNotifications(monitorAlerts);
    const merged = [...mentionItemsMapped, ...monitorItems, ...amsItems, ...statusItems].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    // Mentions carry their own server-side read_at; only the AMS/project
    // items use the client-only readIds set.
    return merged.map((n) => (n.id.startsWith('mention-') ? n : { ...n, read: readIds.has(n.id) }));
  }, [projects, inbox, readIds, mentionItems, monitorAlerts]);

  return {
    notifications,
    unreadCount: notifications.filter((n) => !n.read).length,
    isLoading: projectsLoading || inboxLoading || mentionsLoading,
    markAsRead: (id: string) => {
      if (id.startsWith('mention-')) {
        markMentionRead.mutate(id.replace('mention-', ''));
        return;
      }
      setReadIds((prev) => new Set(prev).add(id));
      if (id.startsWith('ams-incident-')) {
        const incidentId = id.replace('ams-incident-', '');
        void syncIncidentRead(incidentId, () => markAmsIncidentRead(incidentId), () => unmarkRead(id));
      }
    },
    clearAll: () => {
      setReadIds((prev) => {
        const next = new Set(prev);
        buildProjectNotifications(projects).forEach((n) => next.add(n.id));
        buildAmsInboxNotifications(inbox).forEach((n) => next.add(n.id));
        buildMonitoringNotifications(monitorAlerts).forEach((n) => next.add(n.id));
        return next;
      });
      inbox.filter((i) => !i.read_at).forEach((i) => {
        void syncIncidentRead(i.id, () => markAmsIncidentRead(i.id), () => unmarkRead(`ams-incident-${i.id}`));
      });
      mentionItems.filter((n) => !n.read_at).forEach((n) => markMentionRead.mutate(n.id));
    },
  };
}

// API Export for direct usage
export const api = {
  health: fetchHealth,
  workers: {
    list: () => apiClient<{ workers: PlatformWorker[] }>('/workers'),
    get: (id: string) => apiClient<{ worker: PlatformWorker }>(`/workers/${id}`),
  },
  projects: {
    list: () => apiClient<Project[]>('/projects'),
    get: (id: string) => apiClient<Project>(`/projects/${id}`),
    create: (data: Omit<Project, 'id'>) =>
      apiClient<Project>('/projects', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Project>) =>
      apiClient<Project>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => apiClient<void>(`/projects/${id}`, { method: 'DELETE' }),
  },
};